<template>
  <div ref="container" class="cma-asset-3d-view">
    <transition name="cma-asset-3d-view__loading-fade">
      <cma-asset-3d-loading
        v-if="loadOverlay.visible"
        :progress="loadOverlay.progress"
        :asset-label="assetLabel"
        :insights="insights"
      />
    </transition>
  </div>
</template>

<script>
/*
 * Cena Three.js do transformador do CMA, portada do protótipo
 * `feature/visualizador-transformador-weg.html`: mesmas cotas, materiais,
 * iluminação e enquadramentos de câmera.
 *
 * O protótipo roda em three 0.185 e este projeto na 0.128, e QUATRO pontos
 * daqui existem só para compensar a diferença. Nenhum deles parece necessário
 * lendo o código, e sem eles a imagem deixa de ser a da referência:
 *
 * - `srgbColor`, porque a 0.128 não tem color management;
 * - `outputEncoding = sRGBEncoding`, porque `SRGBColorSpace` não existe nela;
 * - `physicallyCorrectLights = true`, que desliga o ×π legado das luzes;
 * - `ROOM_ENVIRONMENT_OFFSET_Y`, porque a sala do addon nasce em outro Y.
 */
/* eslint-disable no-param-reassign */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader';
import { TRANSFORMER_MODELS } from '@/domain/data/cma/transformer3dModels';
import { criarTexturas } from '@/domain/components/cma/cmaAssetTextures';
import themeService from '@/services/themeService';
import {
  box, halfCyl, annulus, srgbColor, TAU,
} from './models/geometriaHelpers';
import { HIGHLIGHT_TARGET } from './models/contrato';
import { criarModeloCad, seguirPecaDoRaio } from './models/transformadorCad';
import { criarPainelDesempenho } from './models/painelDesempenho';
import CmaAsset3dLoading from './CmaAsset3dLoading';
import {
  criarMateriais, criarModeloProcedural, boltEase,
  BOLT_TRAVEL, BOLT_INTERVAL, BOLT_FROM_Y, BOLT_TO_Y, BOLT_PITCH,
  BOLT_EMISSIVE, BOLT_FLICKER_STEPS,
  GAUGE_MIN_ANGLE, GAUGE_MAX_ANGLE, GAUGE_SWEEP_PERIOD,
  GAUGE_TREMOR_AMPLITUDE, GAUGE_TREMOR_PERIOD,
} from './models/transformadorProcedural';
import { criarModeloProceduralCad } from './models/transformadorProceduralCad';
import { buildAuxPanelWall } from './models/paineis';
import { criarMateriaisPaineis } from './models/paineis/materiaisPaineis';

const ASSET_SHIFT_X = -0.9;

/*
 * Tela de espera do CAD real (ver CmaAsset3dLoading.vue e
 * docs/specs/cma-loading-3d-diagnostico.md). Dois ajustes que evitam "vender"
 * uma espera que não existe:
 * - CAD_LOAD_OVERLAY_MIN_DELAY_MS: só aparece se o `.glb` ainda não chegou
 *   depois desse tempo — em cache do navegador ele carrega bem mais rápido
 *   que isso, e o overlay nem chega a piscar na tela.
 * - CAD_LOAD_INDETERMINATE_THRESHOLD: o `onProgress` do GLTFLoader mede
 *   download, não a decodificação Draco no worker — perto de 100% a barra
 *   "trava" por 1-2s nessa fase, então vira indeterminada em vez de fingir
 *   um percentual exato.
 */
const CAD_LOAD_OVERLAY_MIN_DELAY_MS = 400;
const CAD_LOAD_INDETERMINATE_THRESHOLD = 99.5;

// Intervalo de redesenho do painel de desempenho (models/painelDesempenho.js)
// — dissociado do loop de animate()/rAF de propósito: as métricas em si são
// amostradas a cada frame (ver recordPerfFrame), mas escrever no DOM a 60fps
// é custo e ruído visual desnecessários para números pensados para leitura
// humana.
const PERF_HUD_REFRESH_MS = 250;
// Janela de amostras de frame time usada para o FPS médio (ver
// recordPerfFrame) — ~2s a 60fps.
const PERF_HUD_FRAME_SAMPLES = 120;

const UI_AMBER = 0xf9a825;

const LIGHT_GAIN = 0.60;

const KEY_INTENSITY = 2.35;
const FILL_INTENSITY = 0.85;
const RIM_INTENSITY = 0.75;
const HEMISPHERE_INTENSITY = 0.70;
const HEMISPHERE_GROUND_COLOR = 0x111718;

const ENV_MAP_INTENSITY = 0.42;
const TONE_MAPPING_EXPOSURE = 0.92;

/*
 * As cores-base das luzes não mudam entre temas: só a leitura de fundo muda.
 * O que muda de tema para tema é o quanto de preenchimento/ambiente chega no
 * modelo — no tema claro, a cena ao redor (fundo
 * `--color-page-filter-background` e o piso, ver GROUND_COLOR_LIGHT) é muito
 * mais clara que no escuro, e por contraste simultâneo o mesmo material do
 * tanque (cinza médio-escuro) passa a ler como "apagado" se a luz de
 * preenchimento não acompanhar.
 *
 * A `key` (luz de cima, definidora de forma/sombra) É reduzida no tema claro:
 * sua intensidade foi calibrada contra o fundo escuro, e sem baixá-la o
 * reflexo especular que ela cria no topo do tanque/tampa estoura contra o
 * fundo/palco claros, já mais iluminados por fillIntensity/hemisphereIntensity
 * maiores.
 */
const THEME_LIGHT_PROFILES = {
  dark: {
    keyIntensity: KEY_INTENSITY,
    fillIntensity: FILL_INTENSITY,
    rimIntensity: RIM_INTENSITY,
    hemisphereIntensity: HEMISPHERE_INTENSITY,
    hemisphereGroundColor: HEMISPHERE_GROUND_COLOR,
    envMapIntensity: ENV_MAP_INTENSITY,
    // Mesmo valor fixo que já existia em `prateado.userData.envMap`
    // (ver models/transformadorProcedural.js) — tema escuro mantém o look
    // original, só o tema claro ganha um valor próprio abaixo.
    panelEnvMapIntensity: 0.80,
    toneMappingExposure: TONE_MAPPING_EXPOSURE,
  },
  light: {
    keyIntensity: KEY_INTENSITY * 0.7,
    fillIntensity: FILL_INTENSITY * 1.45,
    rimIntensity: RIM_INTENSITY * 1.15,
    hemisphereIntensity: HEMISPHERE_INTENSITY * 1.5,
    hemisphereGroundColor: 0x3a4245,
    envMapIntensity: 0.55,
    // Reduzido: 0.80 (o valor do tema escuro) refletia a sala do PMREM — que
    // não muda com o tema — com contraste alto demais contra o palco/fundo
    // claros, lendo como um reflexo escuro "colado" nas peças prateadas.
    panelEnvMapIntensity: 0.45,
    toneMappingExposure: 1.05,
  },
};

const GROUND_RADIUS = 11.5;
const GROUND_SEGMENTS = 96;
const GROUND_Y = -0.11;
const GROUND_OPACITY = 0.92;
/* O disco esmaece na visão explodida, mas não desaparece: é ele que ancora o
 * emblema (ver applyExplosion) e mantém o mesmo palco nas duas vistas. */
const GROUND_OPACITY_EXPLODED = 0.35;
/*
 * Na explosão, o palco — disco + logo, ver addGround/addGroundLogo — desce
 * sozinho (o ativo não se move em Y além do que PART_EXPLODE já faz peça por
 * peça): abre folga vertical entre a base do ativo e o piso, pra logo
 * centralizada (ver GROUND_LOGO_OFFSET_X/_Z) não ficar embaixo de peça
 * nenhuma, sem precisar mexer no deslocamento individual da base nem
 * levantar o resto do modelo.
 */
const GROUND_EXPLODE_DROP = 3.6;

/*
 * Emblema da marca no centro do disco, em coordenadas do próprio
 * `public/static/imgs/cma-logo-mark.svg`: a elipse de contorno mais os quatro
 * traços do glifo. Os textos "Sigma", "ECM" e "TM" do arquivo ficam de fora.
 *
 * As duas diagonais vêm no SVG como `transform="matrix(...)"` sobre uma linha
 * horizontal; aqui estão com as pontas já resolvidas, senão não há como
 * conferi-las contra o arquivo.
 */
const GROUND_LOGO_SVG = {
  centro: [40, 25.5],
  raio: [24.75, 24.25],
  traco: 2.5,
  segmentos: [
    [29.25, 15.25, 51.75, 15.25],
    [29.25, 37.25, 51.75, 37.25],
    [28.741, 15.250, 39.739, 25.542],
    [33.873, 31.249, 39.735, 25.499],
  ],
};
/*
 * Raio externo do círculo (era 8.0, depois 9.5, depois 8.5). Reduzido de
 * novo, bem mais dessa vez: o "sigmaECM" (ver GROUND_WORDMARK_SCALE) precisa
 * de espaço de verdade entre a borda do círculo e o halo pra não brigar com
 * nenhum dos dois — círculo grande demais não deixa esse vão existir dentro
 * do raio do disco (ver GROUND_RADIUS, 11.5), não importa o quanto se
 * encolha o texto.
 */
const GROUND_LOGO_RADIUS = 5.5;
const GROUND_LOGO_Y = -0.085;
const GROUND_LOGO_THICKNESS = 0.02;
// Emblema centralizado na origem (0,0), embaixo do próprio ativo — pedido de
// produto para ler como um heliporto sob o transformador, não deslocado pra
// nenhum dos lados (ver histórico desta constante para a versão com offset).
const GROUND_LOGO_OFFSET_X = 0;
const GROUND_LOGO_OFFSET_Z = 0;

// Conversão SVG->mundo do emblema, compartilhada com o halo (ver addGround):
// mesma fórmula que `escala`/`largura` calculavam antes dentro de
// addGroundLogo, só que aqui vira constante de módulo pra também dar a
// espessura do traço do halo, que nasce antes do emblema (addGround roda
// primeiro — ver initScene).
const GROUND_LOGO_SCALE = GROUND_LOGO_RADIUS
  / (GROUND_LOGO_SVG.raio[0] + (GROUND_LOGO_SVG.traco / 2));
const GROUND_LOGO_STROKE_WIDTH = GROUND_LOGO_SVG.traco * GROUND_LOGO_SCALE;

// Raio médio do halo — anel decorativo em volta do emblema, dentro do raio do
// disco (ver GROUND_RADIUS, 11.5). A espessura não é uma constante própria:
// usa GROUND_LOGO_STROKE_WIDTH, a mesma do traço do emblema — pedido de
// produto pra ler como o mesmo traço. Não depende mais do raio do círculo
// (era 8.9 pra um raio de 8.0, depois 9.3): é ele, e não o círculo, que
// decide quanto de vão sobra pro texto (ver GROUND_WORDMARK_SCALE/_GAP
// abaixo) — aumentado a pedido de produto, ainda com folga do raio do disco.
const GROUND_HALO_RADIUS = 10.3;

/*
 * "sigmaECM" não usa GROUND_LOGO_SCALE: as letras do arquivo original têm
 * rabichos (g, m) que descem quase tanto quanto o diâmetro do círculo — na
 * escala do círculo, a ponta mais baixa do texto passaria de 15 unidades do
 * centro, furando o halo e o próprio disco. Escala própria, bem menor, só
 * pro texto caber no vão entre o círculo e o halo — fica proporcionalmente
 * menor que no logo original/header, mas inteiro e sem sobrepor nada, que
 * era o pedido (ver print com hierarquia círculo → texto → halo).
 */
const GROUND_WORDMARK_SCALE = 0.135;

/*
 * O texto não nasce encostado na borda do círculo (ver GROUND_WORDMARK_GAP)
 * nem gruda no cálculo de posição do círculo (cx, cy) sem ajuste: como o
 * texto usa uma escala PRÓPRIA (ver acima), reaproveitar direto o
 * deslocamento natural do arquivo original (que assume a MESMA escala pro
 * círculo e pro texto) ou faz o texto ficar dentro do círculo, ou ficar longe
 * demais dele, dependendo de qual escala é maior — nenhuma read pra sorte.
 * Em vez disso, este deslocamento é CALCULADO: pega a borda de baixo do
 * círculo já pronta (GROUND_LOGO_RING_OUTER_Z) e empurra o texto pra
 * encostar nela com só GROUND_WORDMARK_GAP de vão, ponto isolado do raio do
 * círculo ou da escala do texto — os dois podem mudar de novo no futuro sem
 * quebrar esta conta.
 */
const GROUND_WORDMARK_GAP = 0.3;
// Ponto mais alto do texto (mais perto do círculo) no arquivo original: topo
// do "TM" sobrescrito (`GROUND_WORDMARK_TM_D`), y = 53.4883 — um fiapo acima
// do topo do "ECM" (53.616); "sigma" minúsculo começa bem mais abaixo, então
// não é ele quem decide essa distância.
const GROUND_WORDMARK_TOP_SVG_Y = 53.4883;
const GROUND_LOGO_RING_OUTER_Z = GROUND_LOGO_RADIUS
  * (GROUND_LOGO_SVG.raio[1] / GROUND_LOGO_SVG.raio[0]);
const GROUND_WORDMARK_OFFSET_Z = (GROUND_LOGO_RING_OUTER_Z + GROUND_WORDMARK_GAP)
  - ((GROUND_WORDMARK_TOP_SVG_Y - GROUND_LOGO_SVG.centro[1]) * GROUND_WORDMARK_SCALE);

/*
 * Emblema como marca d'água: quase no mesmo tom do piso do tema corrente (ver
 * groundLogoColor), não mais uma cor fixa — no tema claro (piso claro, ver
 * GROUND_COLOR_LIGHT) uma cor escura fixa destoava forte demais do fundo, o
 * oposto do efeito "gravado discretamente na base" que se queria. O delta é o
 * único ajuste de contraste que existe, já que o material é opaco: no tema
 * claro escurece o piso, no escuro clareia.
 *
 * Os dois deltas NÃO são iguais, de propósito: a iluminação da cena (luz +
 * envMap, ver ENV_MAP_INTENSITY) clareia o material renderizado bem além do
 * tom "cru" do hex, e esse efeito pesa muito mais clareando (lerp em direção
 * ao branco) do que escurecendo (multiplicando pra baixo) — um delta de 15%
 * nos dois lados deixava o tema escuro visivelmente mais claro que o piso,
 * quebrando o efeito de marca d'água só nesse tema.
 */
const GROUND_LOGO_WATERMARK_DELTA_LIGHT = 0.15;
const GROUND_LOGO_WATERMARK_DELTA_DARK = 0.015;

/*
 * Degrau de Y entre as camadas do glifo. O emblema é opaco e plano, então nas
 * DUAS juntas reais (o cotovelo esquerdo e o vértice central) as barras se
 * cruzam no mesmo plano e empatariam em profundidade, voltando a cintilar.
 * Com o degrau, a de cima ganha sempre — e 1,5 mm em 2 cm de espessura não
 * aparece em nenhum ângulo que a câmera alcança (ver MAX_POLAR_ANGLE).
 */
const GROUND_LOGO_LAYER_STEP = 0.0015;

/*
 * "sigma" + "ECM" + "TM" sobrescrito do mesmo arquivo master (ver
 * cma-logo-mark.svg) — pedido de produto pra ficar parecido com a logo do
 * cabeçalho, só que na cor do próprio emblema (ver groundLogoColor), não no
 * verde da marca (#008242 no arquivo original).
 *
 * Path `d` copiado ipsis litteris do arquivo — letras têm curvas, geometria à
 * mão (como o resto do emblema) não é viável aqui. Convertido em shape via
 * SVGLoader (ver addGroundLogo): usa DOMParser, não canvas 2D, então continua
 * funcionando no ambiente de teste (que não tem `getContext`, ver comentário
 * em addGroundLogo mais abaixo).
 */
const GROUND_WORDMARK_SIGMA_D = 'M4.32 72.24C3.28667 72.24 2.45667 71.9433 1.83 71.35C1.21 70.75 0.9 69.96 0.9 68.98V68.63H2.35V69.04C2.35 69.5667 2.52667 69.99 2.88 70.31C3.24 70.63 3.71333 70.79 4.3 70.79C4.86667 70.79 5.31667 70.63 5.65 70.31C5.98333 69.99 6.15 69.5267 6.15 68.92C6.15 68.5267 6.07333 68.2267 5.92 68.02C5.77333 67.8067 5.56 67.6467 5.28 67.54C5 67.4333 4.66 67.34 4.26 67.26C3.6 67.12 3.01667 66.9667 2.51 66.8C2.00333 66.6267 1.60667 66.3533 1.32 65.98C1.04 65.6067 0.9 65.05 0.9 64.31C0.9 63.2033 1.20333 62.36 1.81 61.78C2.42333 61.1933 3.22667 60.9 4.22 60.9C5.25333 60.9 6.07333 61.2 6.68 61.8C7.29333 62.3933 7.6 63.1767 7.6 64.15V64.49H6.15V64.1C6.15 63.5667 5.97667 63.1433 5.63 62.83C5.29 62.51 4.82667 62.35 4.24 62.35C3.66667 62.35 3.20667 62.5133 2.86 62.84C2.52 63.16 2.35 63.6233 2.35 64.23C2.35 64.6233 2.42333 64.9267 2.57 65.14C2.72333 65.3467 2.94333 65.5 3.23 65.6C3.51667 65.7 3.86 65.7933 4.26 65.88C4.91333 66.0133 5.49 66.1667 5.99 66.34C6.49 66.5133 6.88333 66.79 7.17 67.17C7.45667 67.55 7.6 68.1133 7.6 68.86C7.6 69.9467 7.3 70.7833 6.7 71.37C6.10667 71.95 5.31333 72.24 4.32 72.24ZM10.0661 72V61.15H11.5161V72H10.0661ZM10.0661 59.94V57.6H11.5161V59.94H10.0661ZM17.423 75.66C16.4096 75.66 15.5863 75.3333 14.953 74.68C14.3263 74.0333 14.013 73.18 14.013 72.12V71.86H15.463V72.17C15.463 72.7767 15.6363 73.2667 15.983 73.64C16.3363 74.02 16.8163 74.21 17.423 74.21C18.0296 74.21 18.513 74.02 18.873 73.64C19.233 73.2667 19.413 72.7767 19.413 72.17V69.19L19.663 69.51C19.4163 69.93 19.0863 70.2567 18.673 70.49C18.2663 70.7233 17.7796 70.84 17.213 70.84C16.6196 70.84 16.083 70.71 15.603 70.45C15.123 70.1833 14.743 69.79 14.463 69.27C14.183 68.75 14.043 68.1133 14.043 67.36V64.37C14.043 63.6233 14.183 62.9933 14.463 62.48C14.743 61.96 15.123 61.5667 15.603 61.3C16.083 61.0333 16.6196 60.9 17.213 60.9C17.7796 60.9 18.2663 61.02 18.673 61.26C19.0863 61.4933 19.4163 61.8167 19.663 62.23L19.413 62.56V61.15H20.863V72.13C20.863 72.8367 20.7163 73.4533 20.423 73.98C20.1296 74.5133 19.7263 74.9267 19.213 75.22C18.6996 75.5133 18.103 75.66 17.423 75.66ZM17.473 69.39C18.0863 69.39 18.563 69.2067 18.903 68.84C19.243 68.4667 19.413 67.9767 19.413 67.37V64.37C19.413 63.7633 19.243 63.2767 18.903 62.91C18.563 62.5367 18.0863 62.35 17.473 62.35C16.853 62.35 16.3663 62.5367 16.013 62.91C15.6663 63.2767 15.493 63.7633 15.493 64.37V67.36C15.493 67.9667 15.6663 68.4567 16.013 68.83C16.3663 69.2033 16.853 69.39 17.473 69.39ZM23.8622 72V61.15H25.3122V62.59L25.0722 62.28C25.3189 61.84 25.6489 61.5 26.0622 61.26C26.4822 61.02 26.9722 60.9 27.5322 60.9C28.2122 60.9 28.8055 61.0733 29.3122 61.42C29.8189 61.7667 30.1755 62.2767 30.3822 62.95L30.0322 62.91C30.2389 62.29 30.5755 61.8 31.0422 61.44C31.5155 61.08 32.0955 60.9 32.7822 60.9C33.3689 60.9 33.8889 61.03 34.3422 61.29C34.7955 61.55 35.1522 61.93 35.4122 62.43C35.6789 62.9233 35.8122 63.52 35.8122 64.22V72H34.3622V64.37C34.3622 63.7633 34.1889 63.2767 33.8422 62.91C33.5022 62.5367 33.0489 62.35 32.4822 62.35C31.9022 62.35 31.4355 62.5367 31.0822 62.91C30.7355 63.2767 30.5622 63.7633 30.5622 64.37V72H29.1122V64.37C29.1122 63.7633 28.9389 63.2767 28.5922 62.91C28.2522 62.5367 27.7989 62.35 27.2322 62.35C26.6522 62.35 26.1855 62.5367 25.8322 62.91C25.4855 63.2767 25.3122 63.7633 25.3122 64.37V72H23.8622ZM41.1602 72.24C40.1602 72.24 39.3868 71.9633 38.8402 71.41C38.2935 70.8567 38.0202 70.1567 38.0202 69.31C38.0202 68.7833 38.1168 68.3467 38.3102 68C38.5035 67.6467 38.7502 67.36 39.0502 67.14C39.3568 66.92 39.6802 66.7433 40.0202 66.61C40.4268 66.4433 40.8402 66.3133 41.2602 66.22C41.6868 66.12 42.0802 66.0167 42.4402 65.91C42.8002 65.7967 43.0902 65.6433 43.3102 65.45C43.5302 65.2567 43.6402 64.9833 43.6402 64.63V64.37C43.6402 63.7633 43.4568 63.2767 43.0902 62.91C42.7302 62.5367 42.2502 62.35 41.6502 62.35C41.0435 62.35 40.5635 62.5367 40.2102 62.91C39.8635 63.2767 39.6902 63.7633 39.6902 64.37V64.67H38.2402V64.42C38.2402 63.3667 38.5535 62.5167 39.1802 61.87C39.8068 61.2233 40.6302 60.9 41.6502 60.9C42.3235 60.9 42.9168 61.05 43.4302 61.35C43.9502 61.6433 44.3568 62.0533 44.6502 62.58C44.9435 63.1067 45.0902 63.72 45.0902 64.42V72H43.6402V70.5L43.8802 70.82C43.6268 71.2733 43.2702 71.6233 42.8102 71.87C42.3502 72.1167 41.8002 72.24 41.1602 72.24ZM41.4602 70.86C42.1402 70.86 42.6735 70.67 43.0602 70.29C43.4468 69.9033 43.6402 69.3533 43.6402 68.64V66.72C43.2335 66.9733 42.7802 67.1467 42.2802 67.24C41.7802 67.3267 41.3035 67.4467 40.8502 67.6C40.4502 67.7333 40.1202 67.9233 39.8602 68.17C39.6002 68.41 39.4702 68.7533 39.4702 69.2C39.4702 69.6867 39.6435 70.0867 39.9902 70.4C40.3368 70.7067 40.8268 70.86 41.4602 70.86Z';
const GROUND_WORDMARK_ECM_D = 'M56.192 54V57.24H51.992V60.84H55.544V64.08H51.992V68.76H56.192V72H48.752V54H56.192ZM67.7602 66.984V67.944C67.7602 69.272 67.3602 70.344 66.5602 71.16C65.7602 71.976 64.6802 72.384 63.3202 72.384C61.9682 72.384 60.8882 71.976 60.0802 71.16C59.2802 70.344 58.8802 69.272 58.8802 67.944V58.056C58.8802 56.72 59.2802 55.648 60.0802 54.84C60.8882 54.024 61.9682 53.616 63.3202 53.616C64.6802 53.616 65.7602 54.024 66.5602 54.84C67.3602 55.648 67.7602 56.72 67.7602 58.056V59.016H64.5202V58.056C64.5202 57.696 64.4162 57.408 64.2082 57.192C64.0002 56.968 63.7042 56.856 63.3202 56.856C62.9362 56.856 62.6402 56.968 62.4322 57.192C62.2242 57.408 62.1202 57.696 62.1202 58.056V67.944C62.1202 68.304 62.2242 68.596 62.4322 68.82C62.6402 69.036 62.9362 69.144 63.3202 69.144C63.7042 69.144 64.0002 69.036 64.2082 68.82C64.4162 68.596 64.5202 68.304 64.5202 67.944V66.984H67.7602ZM70.4739 72V54H73.3539L77.0499 67.944H76.2819L79.9779 54H82.8579V72H79.8579V62.856L80.2419 62.928L77.6979 72H75.6339L73.0899 62.928L73.4739 62.856V72H70.4739Z';
// Superscrito "TM" — encostado no fim do "M" de "ECM" (ver x=85..90, contra o
// 82.86 do fim do M) e um pouco mais alto que o corpo do texto (y=53..56,
// contra o topo do "ECM" em 53.6), do próprio arquivo master.
const GROUND_WORDMARK_TM_D = 'M87.0625 53.793H86.2422V56H85.8906V53.793H85.0703V53.4883H87.0625V53.793ZM90.1227 56H89.7789V53.8203L88.9742 56H88.7008L87.9078 53.8398V56H87.5641V53.4883H88.1148L88.8414 55.5156L89.5719 53.4883H90.1227V56Z';

const GROUND_COLOR_DARK = 0x171a1b;
const GROUND_COLOR_LIGHT = 0xb7afae;

const ROOM_ENVIRONMENT_OFFSET_Y = -3.5;

const ROOM_ENVIRONMENT_LIGHT_INTENSITY = 900;

const CAMERA_FOV = 31;
// Câmera de repouso um pouco mais afastada do alvo (~8% a mais de distância,
// mesma direção — ver HOME_TARGET) pra reduzir o zoom padrão. Escalar
// x/z igualmente preserva a proporção entre eles, então não muda o ângulo
// usado pelo emblema do piso pra ficar de frente pra câmera (ver
// addGroundLogo, `Math.atan2(HOME_POSITION.x, HOME_POSITION.z)`).
const HOME_POSITION = new THREE.Vector3(26.8, 12.6, -19.7);
const HOME_TARGET = new THREE.Vector3(0, 2.2, 0);
const FREE_ORBIT_MIN_DISTANCE = 10;
const FREE_ORBIT_MAX_DISTANCE = 48;
const MAX_POLAR_ANGLE = Math.PI * 0.49;

/*
 * A distância câmera→alvo já é presa por FREE_ORBIT_MIN/MAX_DISTANCE, mas o
 * arrasto lateral move o ALVO, não a distância: sem trava o usuário empurra o
 * transformador pra fora do quadro (e junto os cards ancorados, ver
 * emitAnchors) sem nunca violar o limite de zoom. Então o alvo fica preso a
 * uma caixa em volta de HOME_TARGET, folgada o bastante pra conter todos os
 * FIXED_FRAMINGS (o mais distante fica a ~5,6 do repouso).
 */
const PAN_LIMIT_RADIUS = 8;
const PAN_LIMIT_MIN_Y = 0.5;
const PAN_LIMIT_MAX_Y = 12;

const ORBIT_MIN_DISTANCE = 13;

const CAROUSEL_RADIUS = 16;
const CAROUSEL_DURATION = 1300;
const CAMERA_FOCUS_BASE_DURATION = 480;
const CAMERA_FOCUS_TURN_DURATION = 1100;
const CAMERA_FOCUS_RADIUS_DURATION = 25;
const CAMERA_FOCUS_MIN_DURATION = 520;
const CAMERA_FOCUS_MAX_DURATION = 1500;
const RESET_VIEW_DURATION = 1300;

/*
 * Ao trocar de card (ex.: buchas → resfriamento), interpolar a distância
 * linearmente entre os dois enquadramentos fixos pode, no meio do caminho,
 * aproximar a câmera do corpo do transformador mais do que qualquer um dos
 * dois extremos — a curva de deslocamento "corta caminho" por cima/por
 * dentro do ativo em vez de contorná-lo. Somar um arco (maior no meio,
 * zero nas pontas) à distância força a câmera a se afastar durante o giro,
 * como uma curva por fora, e some de volta ao aproximar do destino.
 */
const CAMERA_FOCUS_RADIUS_BULGE = 4;

const EXPLODE_DURATION = 1300;
const EXPLODE_SCALE = 0.72;
/*
 * Intervalo entre grupos da cascata (ver docs/superpowers/specs/
 * 2026-09-22-cma-explosao-sequencial-design.md §5/§6) — cada grupo ainda
 * leva EXPLODE_DURATION no próprio deslocamento, só o INÍCIO de um grupo
 * para o seguinte é escalonado por este valor. Zero com `reduceMotion` (ver
 * setExplosion): todos os grupos e a subida/descida andam juntos, igual ao
 * comportamento de antes da cascata existir.
 */
const EXPLODE_STAGGER = 350;

/*
 * Ordem MANUAL da cascata, topo -> base (ver buildExplodeGroups) — cada
 * entrada é o nome de uma peça-ÂNCORA que já existe em PART_EXPLODE_CAD
 * (transformadorCad.js), representando o grupo dela (todas as peças com o
 * mesmo vetor de explosão). Nenhuma coordenada é copiada pra cá: o vetor de
 * cada grupo é lido em tempo real do `userData.explode` da própria âncora,
 * então mudar um vetor em PART_EXPLODE_CAD nunca desincroniza esta lista.
 *
 * Pra trocar a ordem: reordena as linhas abaixo. Pra trocar a âncora de um
 * grupo: troca o nome por qualquer outra peça do MESMO vetor — o resultado
 * é idêntico, é só uma referência. Um grupo sem âncora aqui (ex.: os
 * modelos procedurais, só de teste — ver transformadorProcedural.js/
 * transformadorProceduralCad.js) cai no critério antigo (altura média das
 * peças, ver alturaMediaDoGrupo) como reserva, depois de todos os listados.
 */
const EXPLODE_GROUP_ORDER = [
  'Out_polySurface664', // buchas — [1, 2, 1]
  'Out_pCube275', // bolsa - [0, 2, 0]
  'Out_Group_005', // tubos - [0.5, 0, 1]
  'Out_AB01_AB9_pCylinder14', // radiador [1, 0, 11]
  'FloatingFanBank', // ventiladores [1, 0, -11]
  'Out_pCube84', // base - [0, -2, 0]
  'Out_pCube352', // dados eletricos - [4, 0, 1]
];
/*
 * A câmera se afasta do alvo ao EXPANDIR (zoom reverso) e reaproxima ao
 * RETRAIR pelo botão — nos dois casos durante a fase de subida/descida da
 * cascata (ver setExplosion/updateExplosion), nunca fora dela. Ir para casa
 * continua exclusividade do resetView(), que troca esse recuo/reaproximação
 * pelo arco até HOME_POSITION. Soma à distância atual, e não a um valor
 * fixo, pra funcionar tanto vindo da vista livre (home) quanto de uma peça
 * focada (ver focusOnPart) sem duas contas separadas.
 */
const EXPLODE_CAMERA_PULLBACK = 13;


const IDLE_ROTATE_DELAY = 5000;
const IDLE_ROTATE_SPEED = 2;
const HOME_POSITION_EPSILON = 0.05;

const FIXED_ANGLE_PARTS = new Set(['buchas', 'comutador', 'tensao']);
const FOCUS_ADJUSTMENTS = {};

const FIXED_FRAMINGS = {
  buchas: {
    position: new THREE.Vector3(8.348404353268215, 11.667394752799977, 9.286071386255097),
    target: new THREE.Vector3(-0.5284086974160161, 6.240700204819319, -1.9839701889178232),
  },
  tensao: {
    position: new THREE.Vector3(9.305102406320742, 13.543165852525435, 8.791788596383107),
    target: new THREE.Vector3(0.8455815977805401, 5.236486183119727, 0.6110036747290312),
  },
  // Chave chamava-se 'tensaoCarga' — corrigido junto com ANCHORS_CAD em
  // transformadorCad.js (ver comentário lá); ficou órfã aqui até agora.
  temperaturas: {
    position: new THREE.Vector3(3.2568223818744415, 11.786103382660862, 5.951711883273204),
    target: new THREE.Vector3(-2.5763382671953106, 6.565214171206425, -0.27055078265973337),
  },
  resfriamento: {
    position: new THREE.Vector3(10.29493882779763, 9.355815900503906, 20.194683623921588),
    target: new THREE.Vector3(1.4332981454392828, 2.7767574305582734, 4.003233053170098),
  },
  bolsa: {
    position: new THREE.Vector3(-1.4542609045382688, 13.577388093110368, -13.715307124532341),
    target: new THREE.Vector3(-3.682, 6.379, -0.557),
  },
  eletricos: {
    position: new THREE.Vector3(9.617642643351823, 4.671444923438596, -5.385879965691734),
    target: new THREE.Vector3(0.39287040115614386, 1.800196404075822, 0.42660489807527935),
  },
  gas: {
    position: new THREE.Vector3(11.180479906453932, 3.058184061812436, -1.454460158475114),
    target: new THREE.Vector3(1.1926774913778713, 2.6294976247618504, -1.6994721093870608),
  },
  comutador: {
    position: new THREE.Vector3(10.146287003234564, 1.7707680309011888, -7.243990160687057),
    target: new THREE.Vector3(1.2296649773513093, 0.5, -0.4613305399320783),
  },
};
/*
 * Enquadramentos fixos usados só com a cascata expandida (ver explodeTo em
 * focusOnPart) — a peça se afasta das vizinhas nessa hora, então o ângulo
 * calibrado pra FIXED_FRAMINGS (câmera recolhida) fica apertado demais.
 */
const FIXED_FRAMINGS_EXPLODED = {
  bolsa: {
    position: new THREE.Vector3(-1.3703140025779863, 10.30931219329296, -12.209482738950108),
    target: new THREE.Vector3(-5.861431556308515, 7.636449745664509, -0.26532213657111964),
  },
  temperaturas: {
    position: new THREE.Vector3(2.0300859561969262, 12.11693034563396, 8.631460814429525),
    target: new THREE.Vector3(-2.832418276421793, 8.634617853779657, 0.6171266376659538),
  },
  eletricos: {
    position: new THREE.Vector3(13.618807664111714, 4.955685912246841, -5.289511525891683),
    target: new THREE.Vector3(1.697123121197147, 1.2450122833003041, 2.222284878070545),
  },
  resfriamento: {
    position: new THREE.Vector3(14.95324172239183, 12.124722096175288, -15.537790368735447),
    target: new THREE.Vector3(0.5290321348256926, 0.5, -7.982488647052483),
  },
  buchas: {
    position: new THREE.Vector3(7.730627861008711, 14.099008862785556, 6.993403293307143),
    target: new THREE.Vector3(-0.6450489704606455, 7.38686076493628, -4.162504402210329),
  },
  tensao: {
    position: new THREE.Vector3(7.444250697944896, 14.1188757613123, 8.5035344454857),
    target: new THREE.Vector3(-0.21883366302720653, 5.1358908444871, -1.50857277748641),
  },
  comutador: {
    position: new THREE.Vector3(11.733770757967301, 1.5503770551521057, -4.803889524607689),
    target: new THREE.Vector3(2.6699300636911403, 0.5, 0.994324089519923),
  },
  gas: {
    position: new THREE.Vector3(13.172992396139117, 4.142578840695004, 2.5003226836616097),
    target: new THREE.Vector3(2.738307962451272, 1.8432032778357934, -2.914751765078149),
  },
};
const FOCUS_ROTATE_RATIO = 0.4;


const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);

const shortestTurn = (from, to) => {
  const delta = (((to - from) % TAU) + TAU) % TAU;
  return delta > Math.PI ? delta - TAU : delta;
};

const cameraArcDuration = (turn, radiusDelta) => {
  const raw = CAMERA_FOCUS_BASE_DURATION
    + (Math.abs(turn) / Math.PI) * CAMERA_FOCUS_TURN_DURATION
    + Math.abs(radiusDelta) * CAMERA_FOCUS_RADIUS_DURATION;
  return Math.min(CAMERA_FOCUS_MAX_DURATION, Math.max(CAMERA_FOCUS_MIN_DURATION, raw));
};

const arcSpherical = new THREE.Spherical();
const arcOffset = new THREE.Vector3();
const panOffset = new THREE.Vector3();
const panClamped = new THREE.Vector3();

export default {
  name: 'CmaAsset3dView',
  components: { CmaAsset3dLoading },
  props: {
    modelId: {
      type: String,
      required: true,
    },
    highlightedPartId: {
      type: String,
      default: null,
    },
    /** Posição do ativo na lista achatada: é o delta dela que dá o SENTIDO do
     *  giro (ver o watcher e o `direction` de selectModel). */
    assetIndex: {
      type: Number,
      default: -1,
    },
    /** Rótulo do ativo em exibição, repassado à tela de espera do CAD (ver
     *  CmaAsset3dLoading.vue) — este componente não conhece o dashboard. */
    assetLabel: {
      type: String,
      default: '',
    },
    /** Linhas da legenda da tela de espera, já resolvidas pelo componente pai
     *  a partir de dados que o dashboard já carregou (tags, saúde, última
     *  leitura) — ver docs/specs/cma-loading-3d-diagnostico.md §4. */
    insights: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /* Único estado deste componente que precisa de reatividade do Vue: o
       * resto (cena, câmera, modelos...) mora em `created()` de propósito,
       * fora do sistema reativo — ver o comentário grande no topo do
       * arquivo sobre o custo de reatividade em grafos do three.js. */
      loadOverlay: {
        visible: false,
        progress: null,
      },
    };
  },
  created() {
    // Estado (não reativo) de download por modelo do carrossel — todos
    // baixam em paralelo desde `buildModels()`, mas só o ativo (`modelId`)
    // aparece na tela de espera. Ver buildCadModel/refreshLoadOverlay.
    this.cadLoadInfo = {};
    this.overlayFlashTimer = null;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.carousel = null;
    this.slots = [];
    this.animationId = null;
    this.initialCameraPosition = HOME_POSITION.clone();
    this.cameraTarget = HOME_TARGET.clone();
    this.models = [];
    this.reduceMotion = false;
    this.lastFrameAt = 0;
    this.raio = null;
    this.proximoRaio = null;
    this.activeIndex = -1;
    this.spin = null;
    this.cameraAnim = null;
    // Cascata da explosão (ver docs/superpowers/specs/
    // 2026-09-22-cma-explosao-sequencial-design.md) — `explodeGroups` é
    // montado por buildExplodeGroups() (topo -> base), e `groupProgress`/
    // `groupFrom` são paralelos a ele. `liftProgress`/`liftFrom` cobrem o
    // piso, a opacidade do disco e o logotipo, que sobem/descem juntos,
    // fora da cascata por peça (ver updateExplosion).
    this.explodeGroups = [];
    this.groupProgress = [];
    this.groupFrom = [];
    this.liftProgress = 0;
    this.liftFrom = 0;
    this.explodeTo = 0;
    this.explodeStartedAt = null;
    // Deslocamentos da linha do tempo do comando de explosão corrente (ver
    // setExplosion) — junto de `activeStagger`, o único estado que
    // updateExplosion precisa além do relógio para derivar tudo (piso,
    // logotipo e cada grupo) de um `elapsed` só, sem timer por grupo.
    this.liftOffset = 0;
    this.seqOffset = 0;
    this.activeStagger = EXPLODE_STAGGER;
    // Controla o recuo/reaproximação da câmera nas fases de subida/descida
    // (ver EXPLODE_CAMERA_PULLBACK): dispara uma vez por comando de
    // explosão. `viewIsResetting` avisa que é o resetView() quem está
    // levando a câmera pra casa (arco guardado em `pendingHomeArc`, ver
    // resetView/updateExplosion) — e não o recuo/reaproximação do botão.
    this.explodeCameraArcFired = false;
    this.viewIsResetting = false;
    this.pendingHomeArc = null;
    // Troca de ativo pedida (filtro, rodízio) enquanto a vista está
    // explodida: guardada aqui e só executada quando a cascata de retração
    // termina (ver assetIndex watcher/updateExplosion) — sem isto o modelo
    // trocava no meio da peça ainda "voando" de volta pro lugar.
    this.pendingModelSwitch = null;
    this.idleTimer = null;
    this.isAutoRotating = false;
    this.canvasSize = { width: 0, height: 0 };
    this.lastAnchors = '';
    this.materials = null;
    this.textures = null;
    this.environmentTexture = null;
    this.pmrem = null;
    this.highlightMaterials = new Map();
    this.highlightedId = null;
    this.groundFloor = null;
    this.lights = null;
    this.focusedPartId = null;
    this.resizeObserver = null;
    this.auxMaterials = null;
    this.auxPanelWall = null;
    // Painel de debug do controlador SPS (sensorSps.js) — ver
    // buildSpsPanel/syncSpsController. Fica de fora do template/reatividade
    // do Vue DE PROPÓSITO: `.cma-dashboard__card-column` (CmaDashboard.vue)
    // tem z-index maior que o container deste componente, então qualquer
    // z-index interno ao <template> daqui nunca conseguiria ficar por cima
    // dos cards (ex.: o de "buchas") — só escapando pra um elemento anexado
    // direto no `document.body` (ver mounted/beforeDestroy) o painel sai do
    // stacking context da dashboard e sempre renderiza por cima.
    this.spsPanel = null;
    this.spsPanelInputs = null;
    // Painel de desempenho (ver models/painelDesempenho.js) — métricas de
    // renderização são lidas a cada frame em animate() (custo desprezível,
    // são só contadores já mantidos pelo three.js), mas o DOM só é
    // atualizado a cada PERF_HUD_REFRESH_MS via `this.perfInterval`, fora
    // do loop de animação, para não competir com ele.
    this.perfHud = null;
    this.perfLoadStats = {};
    this.perfFrame = {
      lastAt: 0, samples: [], minFps: Infinity, maxFps: 0,
    };
    this.perfInterval = null;
    this.perfGpuInfo = '—';
  },
  computed: {
    /*
     * O getter `theme` da store é a fonte REATIVA: `setDarkTheme`/`setLightTheme`
     * commitam nele no mesmo passo em que viram o atributo do DOM, então lê-lo
     * aqui é o que faz o watcher abaixo disparar na troca de tema em runtime.
     *
     * Sem store (o componente é montado isolado nos testes) cai na leitura
     * direta do DOM, que dá o mesmo valor, só não é reativa.
     */
    isDarkTheme() {
      if (this.$store) return this.$store.getters.theme === 'dark';
      return themeService.isDarkTheme();
    },
  },
  watch: {
    isDarkTheme() {
      this.applyThemeLighting();
    },
    /*
     * O ativo é a fonte da verdade da troca, e não `modelId`: o índice diz
     * para QUAL slot ir e, pelo sinal do próprio delta, para que LADO girar.
     * Avançar na lista gira sempre num sentido, voltar gira no contrário.
     *
     * Pular para um ativo que caia no mesmo slot (1 → 4, com três variantes)
     * não gira nada: é o mesmo modelo, e a ordem se repete de propósito.
     */
    assetIndex(newIndex, oldIndex) {
      if (oldIndex < 0 || newIndex < 0) return;
      const switchModel = () => this.selectModel(this.modelId, {
        animate: true,
        direction: Math.sign(newIndex - oldIndex),
      });
      // Vista explodida (ou ainda retraindo de uma troca anterior): retrai
      // primeiro e só troca de modelo quando a cascata chega em casa — ver
      // pendingModelSwitch/updateExplosion. Sem isto o carrossel girava com
      // as peças ainda separadas.
      if (this.explodeTo > 0.5 || this.explodeStartedAt !== null) {
        this.pendingModelSwitch = switchModel;
        this.setExplosion(0);
        return;
      }
      switchModel();
    },
    highlightedPartId(id) {
      if (id) this.highlightPart(id);
      else this.clearHighlight();
    },
  },
  mounted() {
    this.watchReducedMotion();
    this.initScene();
    // this.buildSpsPanel(); // painel de debug de posição do SPS fora de uso
    // por ora — a posição já foi calibrada (ver SPS_POSITION_CAD em
    // transformadorCad.js); religar se precisar recalibrar ao vivo.
    // this.buildPerfHud(); // painel de desempenho fora de uso — não deve
    // aparecer em tela para o usuário; religar para diagnosticar FPS/memória.
    this.buildModels();
    this.applyThemeLighting();
    this.selectModel(this.modelId, { animate: false });
    this.animate();
    window.addEventListener('resize', this.onWindowResize);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onWindowResize);
      this.resizeObserver.observe(this.$refs.container);
    }
    this.scheduleIdleRotation();
  },
  beforeDestroy() {
    this.clearOverlayFlashTimer();
    if (this.spsPanel) this.spsPanel.remove();
    if (this.perfInterval) clearInterval(this.perfInterval);
    if (this.perfHud) this.perfHud.remover();
    window.removeEventListener('resize', this.onWindowResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.reducedMotionQuery) {
      this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
    }
    cancelAnimationFrame(this.animationId);
    this.clearIdleTimer();

    this.controls.removeEventListener('start', this.onControlsInteractionStart);
    this.controls.removeEventListener('end', this.onControlsInteractionEnd);
    this.controls.dispose();
    this.clearHighlight();
    this.highlightMaterials.forEach(material => material.dispose());
    this.highlightMaterials.clear();
    this.scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        const materiais = Array.isArray(object.material) ? object.material : [object.material];
        // `Material.dispose()` não descarta os mapas (ver decalques dos
        // módulos auxiliares, criarDecalCanvas em models/paineis/) — sem
        // isto o CanvasTexture de cada painel vazaria a cada desmontagem.
        materiais.forEach((m) => {
          if (m.map) m.map.dispose();
          m.dispose();
        });
      }
    });
    if (this.textures) this.textures.descartar();
    if (this.environmentTexture) this.environmentTexture.dispose();
    if (this.pmrem) this.pmrem.dispose();
    this.renderer.dispose();
    this.$refs.container.removeChild(this.renderer.domElement);
  },
  methods: {
    initScene() {
      const { container } = this.$refs;

      this.scene = new THREE.Scene();

      this.camera = new THREE.PerspectiveCamera(
        CAMERA_FOV,
        container.clientWidth / container.clientHeight,
        0.1,
        1000,
      );
      this.camera.position.copy(this.initialCameraPosition);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE;
      this.renderer.shadowMap.enabled = true;
      // PCF, e não PCFSoft: só ele lê `shadow.radius`, de onde sai a penumbra
      this.renderer.shadowMap.type = THREE.PCFShadowMap;
      this.renderer.physicallyCorrectLights = true;
      this.perfGpuInfo = this.readGpuInfo();

      this.pmrem = new THREE.PMREMGenerator(this.renderer);
      const environment = new RoomEnvironment();
      environment.position.y = ROOM_ENVIRONMENT_OFFSET_Y;
      const environmentLight = environment.children.find(child => child.isPointLight);
      environmentLight.intensity = ROOM_ENVIRONMENT_LIGHT_INTENSITY;
      this.environmentTexture = this.pmrem.fromScene(environment, 0.04).texture;
      this.scene.environment = this.environmentTexture;
      environment.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) object.material.dispose();
      });

      const canvas = this.renderer.domElement;
      canvas.setAttribute('role', 'img');
      canvas.setAttribute(
        'aria-label',
        'Modelo 3D interativo do transformador. '
          + 'Arraste para girar, arraste com o botão direito para deslocar '
          + 'e use a rolagem para aproximar ou afastar.',
      );
      canvas.tabIndex = 0;
      container.appendChild(canvas);

      this.canvasSize = { width: container.clientWidth, height: container.clientHeight };

      this.addLights();
      this.addGround();
      this.addGroundLogo();
      // this.addAuxPanelWall(); // BM-2 fora de uso por ora — ver
      // docs/specs/cma-bm2-fidelidade-referencia-html.md (religar quando o
      // módulo estiver alinhado à referência)

      this.carousel = new THREE.Group();
      this.scene.add(this.carousel);

      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.target.copy(this.cameraTarget);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.055;
      this.controls.enableRotate = true;
      this.controls.enablePan = true;
      this.controls.minDistance = FREE_ORBIT_MIN_DISTANCE;
      this.controls.maxDistance = FREE_ORBIT_MAX_DISTANCE;
      this.controls.maxPolarAngle = MAX_POLAR_ANGLE;
      this.controls.autoRotateSpeed = IDLE_ROTATE_SPEED;
      this.controls.update();

      this.controls.addEventListener('start', this.onControlsInteractionStart);
      this.controls.addEventListener('end', this.onControlsInteractionEnd);
    },

    /**
     * Rig do protótipo com o Z ESPELHADO, de propósito: lá a câmera de repouso
     * está em +X+Z e aqui em +X-Z (ver HOME_POSITION). Copiar o Z original
     * poria a key atrás do modelo e apagaria a face que a câmera enquadra.
     */
    addLights() {
      const key = new THREE.DirectionalLight(srgbColor(0xf4f8ff), KEY_INTENSITY * LIGHT_GAIN);
      key.position.set(8, 14, -11);
      key.castShadow = true;
      key.shadow.mapSize.set(2048, 2048);
      key.shadow.camera.left = -14;
      key.shadow.camera.right = 14;
      key.shadow.camera.top = 16;
      key.shadow.camera.bottom = -6;
      key.shadow.camera.far = 60;
      key.shadow.bias = -0.0004;
      key.shadow.normalBias = 0.028;
      key.shadow.radius = 12;
      this.scene.add(key);

      const fill = new THREE.DirectionalLight(srgbColor(0xbddae0), FILL_INTENSITY * LIGHT_GAIN);
      fill.position.set(-10, 8, -6);
      this.scene.add(fill);

      const rim = new THREE.DirectionalLight(srgbColor(0x5faea7), RIM_INTENSITY * LIGHT_GAIN);
      rim.position.set(-4, 10, 11);
      this.scene.add(rim);

      const hemisphere = new THREE.HemisphereLight(
        srgbColor(0xbac8cc), srgbColor(HEMISPHERE_GROUND_COLOR), HEMISPHERE_INTENSITY * LIGHT_GAIN,
      );
      this.scene.add(hemisphere);

      // guardadas para `applyThemeLighting()` reajustar sem recriar as luzes
      this.lights = {
        key, fill, rim, hemisphere,
      };
    },

    groundColor() {
      return this.isDarkTheme ? GROUND_COLOR_DARK : GROUND_COLOR_LIGHT;
    },

    /**
     * Tom do emblema, derivado do piso do tema corrente (ver
     * GROUND_LOGO_WATERMARK_DELTA_LIGHT/_DARK) — marca d'água discreta, nunca
     * uma cor própria que brigue com o piso ou com o ativo.
     */
    groundLogoColor() {
      const base = srgbColor(this.groundColor());
      if (this.isDarkTheme) {
        return base.lerp(srgbColor(0xffffff), GROUND_LOGO_WATERMARK_DELTA_DARK);
      }
      return base.multiplyScalar(1 - GROUND_LOGO_WATERMARK_DELTA_LIGHT);
    },

    /*
     * Único ponto que lê o tema para iluminar a cena: roda uma vez no mount
     * (depois que luzes, chão e materiais já existem) e de novo sempre que
     * `theme` mudar (ver watch), para a troca de tema em runtime não deixar
     * fundo/chão/luz desatualizados enquanto o componente segue montado.
     */
    applyThemeLighting() {
      const profile = THEME_LIGHT_PROFILES[this.isDarkTheme ? 'dark' : 'light'];

      this.scene.background = new THREE.Color(
        themeService.getStyleVar('color-page-filter-background').trim(),
      );

      if (this.groundFloor) {
        this.groundFloor.material.color.copy(srgbColor(this.groundColor()));
        this.groundFloor.material.envMapIntensity = profile.envMapIntensity;
      }

      /* O emblema segue o piso: mesmo material, um degrau de tom de distância.
       * Opacidade não entra aqui de propósito — quem manda nela é a explosão
       * (ver applyExplosion), e reescrevê-la aqui ressuscitaria o disco numa
       * troca de tema com a vista explodida aberta. */
      if (this.groundLogoMaterial) {
        this.groundLogoMaterial.color.copy(this.groundLogoColor());
        this.groundLogoMaterial.envMapIntensity = profile.envMapIntensity;
      }

      // Halo segue a mesma cor fixa do emblema (ver groundLogoColor) — só o
      // envMapIntensity muda com o tema, igual ao resto do palco.
      if (this.groundHaloMaterial) {
        this.groundHaloMaterial.color.copy(this.groundLogoColor());
        this.groundHaloMaterial.envMapIntensity = profile.envMapIntensity;
      }

      if (this.lights) {
        this.lights.key.intensity = profile.keyIntensity * LIGHT_GAIN;
        this.lights.fill.intensity = profile.fillIntensity * LIGHT_GAIN;
        this.lights.rim.intensity = profile.rimIntensity * LIGHT_GAIN;
        this.lights.hemisphere.intensity = profile.hemisphereIntensity * LIGHT_GAIN;
        this.lights.hemisphere.groundColor.copy(srgbColor(profile.hemisphereGroundColor));
      }

      if (this.renderer) {
        this.renderer.toneMappingExposure = profile.toneMappingExposure;
      }

      this.applyEnvMapIntensity(profile.envMapIntensity, profile.panelEnvMapIntensity);
    },

    /** Reaplica `envMapIntensity` nos materiais do modelo sem repetir o
     *  traverse pesado de `buildModel` (UV, sombra, intemperismo). Materiais
     *  com override próprio (ex. `prateado`, ver `userData.envMap`) seguem
     *  `panelValue` em vez de `baseValue` — ver THEME_LIGHT_PROFILES. */
    applyEnvMapIntensity(baseValue, panelValue) {
      [this.materials, this.auxMaterials].forEach((grupo) => {
        if (!grupo) return;
        Object.values(grupo).forEach((material) => {
          const list = Array.isArray(material) ? material : [material];
          list.forEach((item) => {
            if (!item) return;
            item.envMapIntensity = item.userData.envMap === undefined ? baseValue : panelValue;
          });
        });
      });
    },

    addGround() {
      const floor = new THREE.Mesh(
        new THREE.CircleGeometry(GROUND_RADIUS, GROUND_SEGMENTS),
        new THREE.MeshStandardMaterial({
          color: srgbColor(this.groundColor()),
          roughness: 0.88,
          metalness: 0.12,
          envMapIntensity: ENV_MAP_INTENSITY,
          transparent: true,
          opacity: GROUND_OPACITY,
        }),
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = GROUND_Y;
      floor.receiveShadow = true;
      this.scene.add(floor);
      this.groundFloor = floor;

      /*
       * Halo: mesma cor e espessura do traço do emblema (ver
       * groundLogoColor/GROUND_LOGO_STROKE_WIDTH) — pedido de produto pra
       * ler como o mesmo traço, só num raio maior. Material próprio (e não
       * `this.groundLogoMaterial`) porque o emblema ainda não existe nesse
       * ponto — addGround roda antes de addGroundLogo, ver initScene.
       */
      this.groundHaloMaterial = new THREE.MeshStandardMaterial({
        color: this.groundLogoColor(),
        roughness: 0.88,
        metalness: 0.12,
        envMapIntensity: ENV_MAP_INTENSITY,
      });
      const halo = new THREE.Mesh(
        new THREE.RingGeometry(
          GROUND_HALO_RADIUS - (GROUND_LOGO_STROKE_WIDTH / 2),
          GROUND_HALO_RADIUS + (GROUND_LOGO_STROKE_WIDTH / 2),
          GROUND_SEGMENTS,
        ),
        this.groundHaloMaterial,
      );
      halo.rotation.x = -Math.PI / 2;
      halo.position.y = GROUND_LOGO_Y;
      halo.receiveShadow = true;
      this.scene.add(halo);
      this.groundHalo = halo;
    },

    /**
     * Emblema da marca deitado no centro do disco (ver GROUND_LOGO_SVG).
     *
     * Geometria, e não textura: o ambiente de teste não tem canvas 2D
     * (`getContext` devolve null), então rasterizar o SVG deixaria a peça sem
     * como ser verificada.
     *
     * O material é `MeshStandardMaterial` com a MESMA superfície do disco, e
     * não mais um `MeshBasicMaterial` de interface: a intenção é marca gravada
     * na base, então ela tem de receber luz e sombra igual ao piso. É isso que
     * mantém o mesmo delta tonal em todo o glifo — inclusive dentro da sombra
     * do transformador, que cai justo no centro do disco e era onde o emblema
     * translúcido mais se perdia.
     *
     * O giro em Y põe o "para cima" do SVG apontando para longe da câmera de
     * repouso, que é o que deixa o glifo em pé na tela em vez de tombado.
     */
    addGroundLogo() {
      const { centro: [cx, cy], raio: [rx, ry], traco } = GROUND_LOGO_SVG;
      // Mesma conversão SVG->mundo do halo (ver addGround) — constantes de
      // módulo, ver GROUND_LOGO_SCALE/GROUND_LOGO_STROKE_WIDTH.
      const escala = GROUND_LOGO_SCALE;
      const largura = GROUND_LOGO_STROKE_WIDTH;

      /*
       * Opaco de propósito: sem `transparent` não há soma de alpha, então as
       * sobreposições que restam nas juntas não escurecem o verde nem ficam à
       * mercê do empate de profundidade que fazia as juntas cintilarem. O que
       * separa o emblema do piso é só o tom (ver GROUND_LOGO_DARKEN_*), e as
       * camadas se resolvem pelo degrau de Y (ver GROUND_LOGO_LAYER_STEP).
       */
      this.groundLogoMaterial = new THREE.MeshStandardMaterial({
        color: this.groundLogoColor(),
        roughness: 0.88,
        metalness: 0.12,
        envMapIntensity: ENV_MAP_INTENSITY,
      });

      const grupo = new THREE.Group();
      grupo.position.y = GROUND_LOGO_Y;
      grupo.rotation.y = Math.atan2(HOME_POSITION.x, HOME_POSITION.z);

      // contorno: coroa circular no raio maior, achatada em z para a elipse
      // de 24,75 × 24,25 do arquivo
      const contorno = annulus(
        (rx - (traco / 2)) * escala, (rx + (traco / 2)) * escala,
        GROUND_LOGO_THICKNESS, this.groundLogoMaterial,
        GROUND_LOGO_OFFSET_X, 0, GROUND_LOGO_OFFSET_Z, GROUND_SEGMENTS,
      );
      contorno.scale.z = ry / rx;
      grupo.add(contorno);

      /*
       * As duas primeiras entradas são as horizontais e as duas últimas as
       * diagonais (ver GROUND_LOGO_SVG.segmentos). Como só diagonal×horizontal
       * e diagonal×diagonal se cruzam, um degrau por índice já basta para
       * ordenar todas as juntas — o contorno fica no piso do grupo, em 0.
       */
      GROUND_LOGO_SVG.segmentos.forEach(([x1, y1, x2, y2], index) => {
        const ax = ((x1 - cx) * escala) + GROUND_LOGO_OFFSET_X;
        const az = ((y1 - cy) * escala) + GROUND_LOGO_OFFSET_Z;
        const bx = ((x2 - cx) * escala) + GROUND_LOGO_OFFSET_X;
        const bz = ((y2 - cy) * escala) + GROUND_LOGO_OFFSET_Z;
        const comprimento = Math.hypot(bx - ax, bz - az);

        const stroke = new THREE.Group();
        stroke.position.set(
          (ax + bx) / 2, (index + 1) * GROUND_LOGO_LAYER_STEP, (az + bz) / 2,
        );
        stroke.rotation.y = Math.atan2(az - bz, bx - ax);
        stroke.add(box(
          comprimento, GROUND_LOGO_THICKNESS, largura,
          this.groundLogoMaterial, 0, 0, 0,
        ));
        /*
         * Pontas redondas, como o `stroke-linecap="round"` do arquivo. Meias
         * calotas, e não cilindros inteiros: a metade interna de um cilindro
         * centrado na ponta cairia DENTRO da barra, e era essa sobreposição —
         * uma em cada ponta de cada traço — que dava as juntas piscando.
         * Assim a calota só completa o que falta, encostada na barra.
         */
        [
          [-comprimento / 2, Math.PI],
          [comprimento / 2, 0],
        ].forEach(([dx, thetaStart]) => {
          stroke.add(halfCyl(
            largura / 2, GROUND_LOGO_THICKNESS, this.groundLogoMaterial,
            dx, 0, 0, thetaStart,
          ));
        });
        grupo.add(stroke);
      });

      /*
       * "SigmaECM" abaixo do emblema (ver GROUND_WORDMARK_SIGMA_D/_ECM_D).
       * Centralizado em X pela mesma referência (cx) do círculo — nenhum dos
       * dois lados do texto deveria pesar mais que o outro. Já em Z NÃO usa
       * (y - cy) puro: com uma escala própria (ver GROUND_WORDMARK_SCALE),
       * isso desalinharia o texto do círculo pra qualquer lado dependendo de
       * qual escala for maior — o deslocamento certo é o pré-calculado em
       * GROUND_WORDMARK_OFFSET_Z, que encosta o texto na borda do círculo com
       * a folga de GROUND_WORDMARK_GAP.
       *
       * ExtrudeGeometry deixa o shape deitado no plano XY local, extrudado em
       * Z (0..depth) — não é o plano em que o resto do emblema é construído
       * (X/Z do chão, Y pra cima). `rotateX(+90°)` bota o Z da extrusão em pé
       * (vira a espessura, em Y, só que de 0 a -depth) e joga o Y do shape pro
       * eixo Z do mundo, já no sinal certo — bate com a mesma fórmula usada
       * nos traços (`(x - cx) * escala`, `(y - cy) * escala`) sem precisar
       * inverter nenhum eixo na escala. É de propósito que a escala não tem
       * nenhum sinal negativo: um espelhamento (escala negativa num eixo só)
       * inverte a face da geometria sem avisar as normais dela, e a peça
       * passa a receber luz do lado errado — foi o que deixou o texto com tom
       * diferente do resto do emblema numa tentativa anterior.
       */
      const wordmarkSvg = '<svg xmlns="http://www.w3.org/2000/svg">'
        + `<path d="${GROUND_WORDMARK_SIGMA_D}"/>`
        + `<path d="${GROUND_WORDMARK_ECM_D}"/>`
        + `<path d="${GROUND_WORDMARK_TM_D}"/></svg>`;
      const wordmarkY = (GROUND_LOGO_SVG.segmentos.length + 1) * GROUND_LOGO_LAYER_STEP;
      new SVGLoader().parse(wordmarkSvg).paths.forEach((path) => {
        SVGLoader.createShapes(path).forEach((shape) => {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: GROUND_LOGO_THICKNESS,
            bevelEnabled: false,
          });
          geometry.translate(-cx, -cy, 0);
          geometry.rotateX(Math.PI / 2);
          geometry.scale(GROUND_WORDMARK_SCALE, 1, GROUND_WORDMARK_SCALE);
          // `rotateX(+90°)` deixa a extrusão de 0 a -depth (ver comentário
          // acima) — soma o depth de volta pra ela ocupar 0..depth como as
          // outras peças do emblema, só por consistência de convenção.
          geometry.translate(
            GROUND_LOGO_OFFSET_X,
            GROUND_LOGO_THICKNESS + wordmarkY,
            GROUND_LOGO_OFFSET_Z + GROUND_WORDMARK_OFFSET_Z,
          );
          grupo.add(new THREE.Mesh(geometry, this.groundLogoMaterial));
        });
      });

      /* Recebe sombra como o disco (ver addGround), e não projeta: é uma marca
       * NA base, não uma peça sobre ela. */
      grupo.traverse((object) => {
        if (object.isMesh) object.receiveShadow = true;
      });

      this.scene.add(grupo);
      this.groundLogo = grupo;
    },

    /**
     * Parede de módulos auxiliares (BM-2, e os que entrarem a seguir — ver
     * docs/specs/cma-modulos-auxiliares-3d.md §3/§4). Fica direto na cena,
     * fora do carrossel: o modelo ATIVO do carrossel sempre resolve para a
     * origem do mundo com rotação identidade (ver setCarouselAngle), então
     * uma posição fixa de mundo já acompanha o ativo em exibição sem
     * precisar ser filha de nenhum slot.
     *
     * Materiais próprios (`auxMaterials`), separados de `this.materials` do
     * transformador — nomes de chave como `corpo`/`painel` colidiriam com
     * os do modelo procedural, e os dois conjuntos têm ciclos de vida
     * diferentes (a parede não troca com a seleção de ativo).
     */
    addAuxPanelWall() {
      this.auxMaterials = criarMateriaisPaineis();
      this.auxPanelWall = buildAuxPanelWall(this.auxMaterials);
      this.scene.add(this.auxPanelWall);
    },

    buildModels() {
      this.textures = criarTexturas(THREE);
      this.materials = criarMateriais(this.textures);

      this.models = TRANSFORMER_MODELS.map((spec) => {
        // Os slots do CAD que não são o ativo inicial adiam o download/parse
        // (ver buildCadModel/ensureCadModelLoading) — as três variantes
        // usam a MESMA malha (TFMP_MESH, transformer3dModels.js), então
        // montar as três ao mesmo tempo no primeiro carregamento triplica
        // à toa o custo de GLTFLoader.parse()+vestirMalha (todos competindo
        // pela mesma thread) antes do modelo ativo — o único visível de
        // cara — sequer aparecer.
        const model = spec.mesh
          ? this.buildCadModel(spec, { adiar: spec.id !== this.modelId })
          : this.buildProceduralModel(spec);
        model.group.visible = false;

        const slot = new THREE.Group();
        slot.add(model.group);
        this.slots.push(slot);
        this.carousel.add(slot);

        return model;
      });

      this.layoutCarousel();
      this.scene.updateMatrixWorld(true);
      this.buildExplodeGroups();
    },

    /**
     * Monta os grupos da cascata de explosão (ver docs/superpowers/specs/
     * 2026-09-22-cma-explosao-sequencial-design.md §4): peças de TODOS os
     * modelos carregados que compartilham o mesmo vetor `userData.explode`
     * caem no mesmo grupo — é o vetor, e não o nome da peça, que decide
     * para onde ela vai, então é ele quem decide com quem ela sai. Peças
     * paradas (`[0, 0, 0]`) ficam de fora: não ocupam slot na cascata.
     *
     * Chamada em `buildModels()` e de novo no `aoMontar` de cada CAD (ver
     * buildCadModel) — o CAD entra no anel como malha única e só ganha
     * `parts` quando o `.glb` chega, então os grupos são refeitos do zero
     * a cada vez, cobrindo o que já existir em QUALQUER modelo do
     * carrossel (o slot novo já entra com grupos montados).
     *
     * `MO2_sensor` é o único caso que precisa de mapeamento explícito: ele
     * recebe o vetor do `Online_DGA` multiplicado por SENSOR_EXPLODE_LEAD
     * (ver transformadorCad.js) e cairia sozinho num grupo só dele — aqui
     * ele entra no grupo do `Online_DGA`, com o vetor NÃO escalado, mas
     * continua se deslocando pelo seu próprio `userData.explode` (o
     * escalado) em applyExplosion.
     *
     * A ORDEM vem de EXPLODE_GROUP_ORDER (lista manual, editada à mão) —
     * cada entrada é o NOME de uma peça-âncora já existente, e o vetor do
     * grupo dela é lido de `userData.explode` aqui dentro, nunca copiado
     * (ver comentário da constante). Um grupo sem âncora na lista (hoje, só
     * os modelos procedurais de teste) cai por baixo de todos os listados,
     * na ordem antiga (altura média das peças, ver alturaMediaDoGrupo).
     */
    buildExplodeGroups() {
      const porChave = new Map();

      this.models.forEach((model) => {
        Object.keys(model.parts).forEach((id) => {
          const peca = model.parts[id];
          const vetor = (id === 'MO2_sensor' && model.parts.Online_DGA)
            ? model.parts.Online_DGA.userData.explode
            : peca.userData.explode;
          if (vetor.lengthSq() === 0) return;

          const chave = `${vetor.x},${vetor.y},${vetor.z}`;
          if (!porChave.has(chave)) porChave.set(chave, { chave, pecas: [] });
          porChave.get(chave).pecas.push(peca);
        });
      });

      const posicaoManual = new Map();
      EXPLODE_GROUP_ORDER.forEach((nomeAncora, index) => {
        this.models.some((model) => {
          const ancora = model.parts[nomeAncora];
          if (!ancora) return false;
          const { explode } = ancora.userData;
          posicaoManual.set(`${explode.x},${explode.y},${explode.z}`, index);
          return true;
        });
      });

      const grupos = Array.from(porChave.values()).map(grupo => ({
        ...grupo,
        alturaY: this.alturaMediaDoGrupo(grupo.pecas),
      }));

      const listados = grupos.filter(grupo => posicaoManual.has(grupo.chave));
      const naoListados = grupos.filter(grupo => !posicaoManual.has(grupo.chave));
      listados.sort((a, b) => posicaoManual.get(a.chave) - posicaoManual.get(b.chave));
      naoListados.sort((a, b) => b.alturaY - a.alturaY);

      this.explodeGroups = [...listados, ...naoListados];
      // Grupos recém-criados nascem já no alvo corrente — evita que uma
      // peça que acabou de chegar (troca de modelo, .glb que terminou de
      // carregar) "pule" de repouso para o meio da cascata antes de ter
      // slot próprio.
      this.groupProgress = this.explodeGroups.map(() => this.explodeTo);
      this.groupFrom = this.explodeGroups.map(() => this.explodeTo);
    },

    /** Altura média (Y, em espaço de mundo) das peças de um grupo — decide
     *  a ordem da cascata (ver buildExplodeGroups). Bounding box, e não
     *  `userData.home.y`: nos modelos procedurais (transformadorProcedural
     *  .js/transformadorProceduralCad.js) o grupo de cada peça fica na
     *  origem — a altura real vem de deslocamentos embutidos nas malhas —,
     *  então `home.y` empataria todo mundo em zero. */
    alturaMediaDoGrupo(pecas) {
      const caixa = new THREE.Box3();
      const soma = pecas.reduce((total, peca) => {
        caixa.setFromObject(peca);
        return total + (caixa.min.y + caixa.max.y) / 2;
      }, 0);
      return soma / pecas.length;
    },

    /**
     * Escolhe entre os dois builders proceduais (código, sem `.glb`):
     * `criarModeloProcedural` (Transformador 2 clássico) e
     * `criarModeloProceduralCad` (layout do Transformador 1, spec
     * cma-transformador1-para-procedural-potencia3.md) — a escolha é o
     * discriminador `spec.tipo`, hoje só usado por `potencia-3` (ver
     * transformer3dModels.js).
     */
    buildProceduralModel(spec) {
      const contexto = {
        materials: this.materials,
        deslocamentoX: ASSET_SHIFT_X,
        envMapIntensity: ENV_MAP_INTENSITY,
      };
      return spec.tipo === 'proceduralCad'
        ? criarModeloProceduralCad(spec, contexto)
        : criarModeloProcedural(spec, contexto);
    },

    /**
     * Liga o modelo do CAD (ver models/transformadorCad.js) à cena. O módulo é
     * puro — não conhece o componente —, então tudo que ele precisa da cena
     * entra por aqui: a intensidade de ambiente do tema, o deslocamento do
     * ativo no palco, e três avisos.
     *
     * `seguirVivo` existe porque o download é longo: `this.renderer` só é nulo
     * depois de `beforeDestroy`, e é o sinal de que não há mais cena para
     * receber a malha.
     */
    buildCadModel(spec, { adiar = false } = {}) {
      this.cadLoadInfo[spec.id] = {
        percent: null,
        indeterminate: true,
        done: false,
        error: false,
      };
      return criarModeloCad(spec, {
        materials: this.materials,
        envMapIntensity: ENV_MAP_INTENSITY,
        deslocamentoX: ASSET_SHIFT_X,
        seguirVivo: () => Boolean(this.renderer),
        adiarCarregamento: adiar,
        aoProgredir: evento => this.onCadProgress(spec.id, evento),
        aoErrar: (erro) => {
          this.onCadSettled(spec.id, { error: true });
          this.$emit('mesh-error', erro);
        },
        aoMontar: (statsCarregamento) => {
          this.scene.updateMatrixWorld(true);
          // O SPS_controller (sensorSps.js) só existe depois que o .glb
          // termina de carregar — se este CAD já é o ativo, o painel de
          // debug (ver buildSpsPanel/syncSpsController) ainda estava
          // escondido na hora do primeiro setActive.
          this.syncSpsController();
          this.buildExplodeGroups();
          this.onCadSettled(spec.id, { done: true });
          this.perfLoadStats[spec.id] = statsCarregamento;
        },
      });
    },

    /** `event.loaded/event.total` do GLTFLoader vira a tela de espera do CAD
     *  (ver CmaAsset3dLoading.vue) — `total` vem `0` quando o servidor não
     *  envia `Content-Length`, e cai em modo indeterminado. */
    onCadProgress(id, { loaded, total }) {
      const info = this.cadLoadInfo[id];
      if (!info || info.done || info.error) return;
      if (total > 0) {
        info.percent = Math.min(100, (loaded / total) * 100);
        info.indeterminate = info.percent >= CAD_LOAD_INDETERMINATE_THRESHOLD;
      } else {
        info.percent = null;
        info.indeterminate = true;
      }
      if (id === this.modelId) this.refreshLoadOverlay();
    },

    onCadSettled(id, patch) {
      const info = this.cadLoadInfo[id];
      if (!info) return;
      Object.assign(info, patch);
      if (id === this.modelId) this.refreshLoadOverlay();
    },

    /** Dispara o download/parse adiado de um slot do CAD (ver `adiar` em
     *  buildCadModel/buildModels) na primeira vez que ele é selecionado —
     *  só então `iniciarCarregamento` existe no modelo. Nas trocas
     *  seguintes para o mesmo slot é um no-op. */
    ensureCadModelLoading(model) {
      if (model && typeof model.iniciarCarregamento === 'function') {
        model.iniciarCarregamento();
        model.iniciarCarregamento = null;
      }
    },

    /** Espelha o estado (não reativo) de download do CAD ativo em
     *  `loadOverlay` (reativo), com o atraso mínimo de exibição — evita
     *  "flash" de UI quando o `.glb` já está em cache do navegador (ver
     *  CAD_LOAD_OVERLAY_MIN_DELAY_MS). Em erro, o overlay some direto e cede
     *  lugar ao estado de erro (ver `mesh-error`, emitido em buildCadModel) —
     *  a tela de espera nunca mascara uma falha real. */
    refreshLoadOverlay() {
      const info = this.cadLoadInfo[this.modelId];
      if (!info || info.done || info.error) {
        this.clearOverlayFlashTimer();
        this.loadOverlay.visible = false;
        this.loadOverlay.progress = null;
        return;
      }

      this.loadOverlay.progress = info.indeterminate ? null : info.percent;

      if (!this.loadOverlay.visible && !this.overlayFlashTimer) {
        this.overlayFlashTimer = setTimeout(() => {
          this.overlayFlashTimer = null;
          const current = this.cadLoadInfo[this.modelId];
          if (current && !current.done && !current.error) this.loadOverlay.visible = true;
        }, CAD_LOAD_OVERLAY_MIN_DELAY_MS);
      }
    },

    clearOverlayFlashTimer() {
      if (!this.overlayFlashTimer) return;
      clearTimeout(this.overlayFlashTimer);
      this.overlayFlashTimer = null;
    },

    watchReducedMotion() {
      if (!window.matchMedia) return;
      const consulta = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = consulta.matches;
      if (!consulta.addEventListener) return;
      this.reducedMotionQuery = consulta;
      this.onReducedMotionChange = (evento) => { this.reduceMotion = evento.matches; };
      consulta.addEventListener('change', this.onReducedMotionChange);
    },

    slotAngle(index) {
      return (index / this.models.length) * TAU;
    },

    carouselAngleFor(index) {
      return -this.slotAngle(index);
    },

    layoutCarousel() {
      this.carousel.position.set(0, 0, -CAROUSEL_RADIUS);
      this.slots.forEach((slot, index) => {
        const angle = this.slotAngle(index);
        slot.position.set(
          Math.sin(angle) * CAROUSEL_RADIUS,
          0,
          Math.cos(angle) * CAROUSEL_RADIUS,
        );
      });
      this.setCarouselAngle(0);
    },

    /** Gira o anel e CONTRA-GIRA cada posição, para o modelo transladar pelo
     *  arco sem rodar no próprio eixo. */
    setCarouselAngle(angle) {
      this.carousel.rotation.y = angle;
      this.slots.forEach((slot) => {
        slot.rotation.y = -angle;
      });
    },

    turnFor(from, to, direction) {
      const total = this.models.length;
      if (!direction) return shortestTurn(this.carousel.rotation.y, this.carouselAngleFor(to));
      const forward = direction > 0;
      const steps = (((forward ? to - from : from - to) % total) + total) % total;
      return (forward ? -steps : steps) * (TAU / total);
    },

    selectModel(id, { animate, direction } = {}) {
      const index = this.models.findIndex(model => model.id === id);
      if (index < 0 || index === this.activeIndex) return;

      this.ensureCadModelLoading(this.models[index]);
      this.refreshLoadOverlay();
      this.clearHighlight();
      this.focusedPartId = null;
      this.cameraAnim = null;
      this.clearIdleTimer();
      this.stopIdleRotation();
      this.controls.minDistance = FREE_ORBIT_MIN_DISTANCE;
      this.controls.maxDistance = FREE_ORBIT_MAX_DISTANCE;
      if (this.spin) this.finishSpin();

      if (!animate || this.reduceMotion) {
        this.setCarouselAngle(this.carouselAngleFor(index));
        this.setActive(index);
        this.showModels([index]);
        return;
      }

      const fromAngle = this.carousel.rotation.y;
      const delta = this.turnFor(this.activeIndex, index, direction);
      this.spin = {
        from: this.activeIndex,
        to: index,
        fromAngle,
        delta,
        startedAt: performance.now(),
      };
      this.setActive(index);
      this.showModels([this.spin.from, index]);
      this.$emit('switching', true);
    },

    setActive(index) {
      this.clearCurrentBolt();
      this.activeIndex = index;
      this.lastAnchors = '';
      this.syncSpsController();
      // Mín/máx e amostras de FPS são por modelo em exibição — sem isto, a
      // troca de ativo herdaria o mín/máx do anterior e o "diagnóstico
      // justo" entre dois GLBs ficaria contaminado pelo histórico errado.
      this.perfFrame = {
        lastAt: 0, samples: [], minFps: Infinity, maxFps: 0,
      };
    },

    /** Controlador SPS (sensorSps.js) do modelo ATIVO no carrossel, se
     *  houver — só o CAD do transformador com sensor MO-2 ganha um (ver
     *  transformadorCad.js), os modelos procedurais não. */
    getSpsController() {
      const model = this.models[this.activeIndex];
      return (model && model.parts && model.parts.SPS_controller) || null;
    },

    /**
     * Painel de debug do controlador SPS: um elemento à parte, anexado direto
     * em `document.body` (e não no <template> deste componente) — ver o
     * comentário em `created()` sobre por que isso é necessário (stacking
     * context dos cards do CmaDashboard.vue). Construído uma vez, aqui; quem
     * liga/desliga a visibilidade e atualiza os valores é `syncSpsController`.
     */
    buildSpsPanel() {
      const panel = document.createElement('div');
      panel.className = 'sps-position-panel';
      panel.style.display = 'none';
      // Bloqueia o evento na origem, antes que o OrbitControls (preso ao
      // canvas) o veja — arrastar um slider não pode também girar/panorâmicar
      // a câmera por baixo do painel.
      ['pointerdown', 'mousedown', 'wheel'].forEach((tipo) => {
        panel.addEventListener(tipo, evento => evento.stopPropagation());
      });

      const titulo = document.createElement('strong');
      titulo.className = 'sps-position-panel__title';
      titulo.textContent = 'Posição SPS (debug)';
      panel.appendChild(titulo);

      const inputs = {};
      ['x', 'y', 'z'].forEach((eixo) => {
        const linha = document.createElement('label');
        linha.className = 'sps-position-panel__row';

        const rotulo = document.createElement('span');
        rotulo.textContent = eixo.toUpperCase();

        const input = document.createElement('input');
        input.type = 'range';
        // Deslocamento em METROS a partir da posição de origem
        // (`userData.home`, ver transformadorCad.js), e não coordenada
        // absoluta do mundo — ela pode valer qualquer coisa dependendo de
        // onde o CAD planta o sensor MO-2, e um range fixo pequeno demais
        // clampava o `value` do input silenciosamente. Offset sempre começa
        // em 0 (dentro do range), então nunca clampa na abertura do painel.
        input.min = '-5';
        input.max = '5';
        input.step = '0.001';

        // Mostra a posição ABSOLUTA (a mesma que sai de `sps.position.set(...)`
        // em transformadorCad.js), não o offset cru do slider — é o valor que
        // interessa pra calibrar a peça, e é ele que já aparece pronto na
        // abertura do painel (ver syncSpsController), sem precisar mexer em
        // nada primeiro.
        const valor = document.createElement('span');
        valor.className = 'sps-position-panel__value';

        input.addEventListener('input', () => {
          this.applySpsPosition();
          const controller = this.getSpsController();
          if (controller) valor.textContent = controller.position[eixo].toFixed(3);
        });

        linha.appendChild(rotulo);
        linha.appendChild(input);
        linha.appendChild(valor);
        panel.appendChild(linha);

        inputs[eixo] = { input, valor };
      });

      const salvar = document.createElement('button');
      salvar.type = 'button';
      salvar.className = 'sps-position-panel__save';
      salvar.textContent = 'Salvar posição';
      salvar.addEventListener('click', () => this.saveSpsPosition());
      panel.appendChild(salvar);

      document.body.appendChild(panel);
      this.spsPanel = panel;
      this.spsPanelInputs = inputs;
    },

    /** Reflete a existência/posição do controlador SPS do ativo corrente no
     *  painel de debug (ver buildSpsPanel) — chamado sempre que o ativo muda
     *  (ver setActive) e ao terminar de montar um CAD (ver buildCadModel,
     *  aoMontar), já que o SPS_controller só existe depois do .glb carregar.
     *  O SLIDER usa o DESLOCAMENTO a partir de `userData.home` (ver
     *  applySpsPosition) — não a posição absoluta, que pode estourar o range
     *  fixo do input e ser clampada silenciosamente pelo navegador. Já o
     *  VALOR exibido é a posição absoluta, a mesma calculada em
     *  transformadorCad.js — é ela que já aparece pronta na abertura do
     *  painel, antes de qualquer slider ser tocado. */
    syncSpsController() {
      // Painel de debug desligado (ver mounted, buildSpsPanel comentado) —
      // nada aqui pra sincronizar.
      if (!this.spsPanel) return;
      const controller = this.getSpsController();
      this.spsPanel.style.display = controller ? 'flex' : 'none';
      if (!controller) return;
      const { home } = controller.userData;
      ['x', 'y', 'z'].forEach((eixo) => {
        const { input, valor } = this.spsPanelInputs[eixo];
        input.value = controller.position[eixo] - home[eixo];
        valor.textContent = controller.position[eixo].toFixed(3);
      });
    },

    /** Aplica ao vivo a posição dos sliders no controlador SPS do ativo
     *  corrente — cada slider é um DESLOCAMENTO em metros a partir de
     *  `userData.home` (posição de origem, plantada em transformadorCad.js),
     *  somado aqui, e não a posição absoluta em si (ver syncSpsController). */
    applySpsPosition() {
      const controller = this.getSpsController();
      if (!controller) return;
      const { home } = controller.userData;
      const { x, y, z } = this.spsPanelInputs;
      controller.position.set(
        home.x + Number(x.input.value),
        home.y + Number(y.input.value),
        home.z + Number(z.input.value),
      );
    },

    /** String de identificação da GPU (ex.: "ANGLE (NVIDIA, ... Direct3D11)")
     *  via `WEBGL_debug_renderer_info` — extensão nem sempre disponível
     *  (bloqueada por alguns navegadores/flags de privacidade), daí o
     *  fallback para o parâmetro padrão `gl.RENDERER` (menos específico,
     *  geralmente só "WebGL"), e o texto fixo se nem isso existir. */
    readGpuInfo() {
      try {
        const gl = this.renderer.getContext();
        const ext = gl.getExtension('WEBGL_debug_renderer_info');
        if (ext) return String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL));
        return String(gl.getParameter(gl.RENDERER));
      } catch (erro) {
        return 'indisponível';
      }
    },

    /** Cria o painel de desempenho (ver models/painelDesempenho.js) e liga o
     *  intervalo que o redesenha — ver PERF_HUD_REFRESH_MS. Chamada comentada
     *  em mounted() (fora de uso); religar ali para diagnosticar ao vivo. */
    buildPerfHud() {
      this.perfHud = criarPainelDesempenho();
      this.refreshPerfHud();
      this.perfInterval = setInterval(this.refreshPerfHud, PERF_HUD_REFRESH_MS);
    },

    /** Amostra o tempo do frame que acabou de renderizar (ver animate()) —
     *  barato (um subtração e um push num array limitado), roda a cada
     *  frame de propósito, diferente da escrita no DOM (ver refreshPerfHud),
     *  que é mais cara e não precisa da resolução de um frame. */
    recordPerfFrame(agora) {
      const frame = this.perfFrame;
      if (frame.lastAt) {
        const delta = agora - frame.lastAt;
        if (delta > 0) {
          const fps = 1000 / delta;
          frame.samples.push(delta);
          if (frame.samples.length > PERF_HUD_FRAME_SAMPLES) frame.samples.shift();
          if (fps < frame.minFps) frame.minFps = fps;
          if (fps > frame.maxFps) frame.maxFps = fps;
        }
      }
      frame.lastAt = agora;
    },

    /** Formata as métricas de carregamento do `.glb` ativo (ver
     *  models/transformadorCad.js, obterTimingCarregamento) para exibição —
     *  `null`/parcial enquanto o download ainda não terminou, ou para
     *  modelos procedurais (sem `.glb`, ver TRANSFORMER_MODELS). */
    formatPerfLoadStats(stats) {
      if (!stats) return '—';
      const partes = [];
      if (Number.isFinite(stats.duracaoMs)) partes.push(`${stats.duracaoMs.toFixed(0)} ms`);
      if (stats.bytesTotais) partes.push(`${(stats.bytesTotais / (1024 * 1024)).toFixed(1)} MB`);
      if (stats.triangulos) partes.push(`${Math.round(stats.triangulos).toLocaleString('pt-BR')} tri`);
      return partes.length ? partes.join(' · ') : '—';
    },

    /** `performance.memory` só existe em navegadores baseados em Chromium
     *  (flag não-padrão) — nos demais (Firefox, Safari) o painel mostra
     *  "n/d" em vez de um número inventado. */
    formatPerfJsHeap() {
      const mem = window.performance && window.performance.memory;
      if (!mem) return 'n/d';
      return `${(mem.usedJSHeapSize / (1024 * 1024)).toFixed(0)} MB / `
        + `${(mem.jsHeapSizeLimit / (1024 * 1024)).toFixed(0)} MB`;
    },

    /** Redesenha o painel de desempenho a partir do estado acumulado (ver
     *  recordPerfFrame/perfLoadStats) e de `renderer.info` (contadores que o
     *  próprio three.js já mantém, sem custo extra de medir). Chamado pelo
     *  `perfInterval` (ver buildPerfHud) — NÃO pelo loop de animate(), de
     *  propósito (ver PERF_HUD_REFRESH_MS). */
    refreshPerfHud() {
      // `renderer.info` é sempre real do three.js num navegador — só falta
      // nos testes deste componente, que trocam `THREE.WebGLRenderer` por um
      // dublê sem essa API (ver CmaAsset3dView.spec.js).
      if (!this.perfHud || !this.renderer || !this.renderer.info) return;

      const { samples, minFps, maxFps } = this.perfFrame;
      const ultimoDelta = samples[samples.length - 1];
      const mediaDelta = samples.length
        ? samples.reduce((soma, valor) => soma + valor, 0) / samples.length
        : 0;
      const fpsAtual = ultimoDelta ? 1000 / ultimoDelta : 0;
      const fpsMedio = mediaDelta ? 1000 / mediaDelta : 0;

      const spec = TRANSFORMER_MODELS.find(item => item.id === this.modelId);
      const { info } = this.renderer;

      this.perfHud.atualizar([
        `Modelo:       ${spec ? spec.label : this.modelId} (${this.modelId})`,
        `GLB:          ${spec && spec.mesh ? spec.mesh.url.split('/').pop() : '— (procedural)'}`,
        `Carregamento: ${this.formatPerfLoadStats(this.perfLoadStats[this.modelId])}`,
        '',
        `FPS agora:    ${fpsAtual.toFixed(0)}`,
        `FPS médio:    ${fpsMedio.toFixed(0)} (mín ${Number.isFinite(minFps) ? minFps.toFixed(0) : '—'}`
          + ` / máx ${maxFps ? maxFps.toFixed(0) : '—'})`,
        `Frame:        ${mediaDelta.toFixed(2)} ms`,
        '',
        `Draw calls:   ${info.render.calls}`,
        `Triângulos:   ${Math.round(info.render.triangles).toLocaleString('pt-BR')}`,
        `Geometrias:   ${info.memory.geometries}`,
        `Texturas:     ${info.memory.textures}`,
        `Shaders:      ${info.programs ? info.programs.length : '—'}`,
        '',
        `Resolução:    ${this.canvasSize.width}×${this.canvasSize.height}`
          + ` @ ${this.renderer.getPixelRatio().toFixed(2)}x`,
        `Memória JS:   ${this.formatPerfJsHeap()}`,
        `GPU:          ${this.perfGpuInfo}`,
      ]);
    },

    /** Só imprime a posição atual no console — pedido de produto era "salvar
     *  a posição no console", não persistir em nenhum lugar; quem calibra a
     *  peça lê o valor aqui e copia pra SPS_LATERAL_OFFSET (ver
     *  transformadorCad.js) manualmente. */
    saveSpsPosition() {
      const controller = this.getSpsController();
      if (!controller) return;
      // eslint-disable-next-line no-console
      console.log('Posição do controlador SPS:', {
        x: controller.position.x,
        y: controller.position.y,
        z: controller.position.z,
      });
    },

    showModels(indices) {
      this.models.forEach((model, i) => {
        model.group.visible = indices.indexOf(i) >= 0;
      });
    },

    finishSpin() {
      const { spin } = this;
      this.spin = null;
      if (!spin) return;

      this.setCarouselAngle(this.carouselAngleFor(spin.to));
      this.setActive(spin.to);
      this.showModels([spin.to]);
      this.$emit('switching', false);
      this.scheduleIdleRotation();
    },

    updateCarousel() {
      const { spin } = this;
      if (!spin) return;

      const t = Math.min(1, (performance.now() - spin.startedAt) / CAROUSEL_DURATION);
      if (t >= 1) {
        this.finishSpin();
        return;
      }

      this.setCarouselAngle(spin.fromAngle + spin.delta * easeInOut(t));
    },

    updateFans(agora) {
      const anterior = this.lastFrameAt || agora;
      this.lastFrameAt = agora;
      if (this.reduceMotion) return;
      // piso em zero: o primeiro quadro depois de uma remontagem chega com o
      // relógio do quadro anterior, e um passo negativo giraria ao contrário
      const passo = Math.max(0, Math.min(100, agora - anterior)) / 1000;
      this.models.forEach((model) => {
        model.helices.forEach((helice) => {
          helice.rotation.y += helice.userData.giro * passo;
        });
      });
    },

    updateGauges(agora) {
      const t = agora / 1000;
      const onda = periodo => Math.sin((TAU * t) / periodo);
      const varredura = (1 - Math.cos((TAU * t) / GAUGE_SWEEP_PERIOD)) / 2;
      const angulo = GAUGE_MIN_ANGLE
        + (GAUGE_MAX_ANGLE - GAUGE_MIN_ANGLE) * varredura
        + onda(GAUGE_TREMOR_PERIOD) * GAUGE_TREMOR_AMPLITUDE;
      this.models.forEach((model) => {
        model.ponteiros.forEach((ponteiro) => {
          ponteiro.rotation.z = this.reduceMotion ? ponteiro.userData.repouso : angulo;
        });
      });
    },

    updateCurrent(agora) {
      const model = this.models[this.activeIndex];
      if (!model || !model.raios.length) return;

      if (this.reduceMotion) {
        this.clearCurrentBolt();
        return;
      }

      if (this.raio) {
        const t = (agora - this.raio.de) / BOLT_TRAVEL;
        if (t >= 1) {
          this.clearCurrentBolt();
          this.proximoRaio = agora + this.randomBoltDelay();
          return;
        }
        const { pivo } = this.raio;
        const { percurso } = pivo.userData;
        const subida = (BOLT_TO_Y - BOLT_FROM_Y) * boltEase(t);
        percurso.position.y = BOLT_FROM_Y + subida;
        pivo.rotation.y = this.raio.angulo - (TAU * subida) / BOLT_PITCH;
        this.materials.raio.emissiveIntensity = Math.floor(t * BOLT_FLICKER_STEPS) % 2 === 0
          ? BOLT_EMISSIVE
          : BOLT_EMISSIVE * 0.45;
        return;
      }

      if (this.proximoRaio === null) {
        this.proximoRaio = agora + this.randomBoltDelay();
        return;
      }
      if (agora < this.proximoRaio) return;

      const pivo = model.raios[Math.floor(Math.random() * model.raios.length)];
      const angulo = Math.random() * TAU;
      pivo.rotation.y = angulo;
      pivo.userData.percurso.position.y = BOLT_FROM_Y;
      pivo.visible = true;
      this.raio = { pivo, de: agora, angulo };
    },

    randomBoltDelay() {
      const [minimo, maximo] = BOLT_INTERVAL;
      return minimo + Math.random() * (maximo - minimo);
    },

    clearCurrentBolt() {
      if (!this.raio) return;
      this.raio.pivo.visible = false;
      this.raio = null;
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      const agora = performance.now();
      this.updateFans(agora);
      this.updateGauges(agora);
      this.updateCurrent(agora);
      this.updateCarousel();
      this.updateCameraFocus();
      this.updateExplosion();
      this.controls.update();
      // Depois do update, senão a inércia do arrasto (enableDamping) repõe no
      // frame seguinte o que a trava tirou. Enquanto um arco roda, quem manda
      // no alvo é updateCameraFocus.
      if (!this.cameraAnim) this.clampPanTarget();
      this.renderer.render(this.scene, this.camera);
      this.recordPerfFrame(agora);
      this.emitAnchors();
    },

    /**
     * Expandir nunca volta pra casa: parte de qualquer ângulo em que a
     * câmera esteja (vista livre em giro/zoom do usuário, ou card
     * selecionado enquadrando uma peça) e explode direto dali — o usuário
     * assiste o conjunto (ou a peça focada) se separando a partir do ponto
     * de vista que ele já escolheu.
     *
     * Só o retrair (ver resetView) devolve a câmera pra casa.
     */
    toggleExplosion() {
      if (this.explodeTo > 0.5) {
        this.setExplosion(0);
        return;
      }

      this.setExplosion(1);
    },

    /**
     * Tira o snapshot de onde a cascata está (cada grupo, o piso/logotipo, e
     * se estava a caminho de outro alvo) e define os dois deslocamentos da
     * linha do tempo (ver docs/superpowers/specs/
     * 2026-09-22-cma-explosao-sequencial-design.md §6):
     *
     * - EXPANDIR: a subida (piso + câmera) acontece primeiro, sozinha; a
     *   cascata começa só depois que ela termina.
     * - RETRAIR: a cascata acontece primeiro; a descida ocupa a última
     *   janela, do tamanho de uma peça só.
     *
     * Com `activeStagger` zerado (reduceMotion), as duas janelas colapsam
     * numa só: piso e todos os grupos andam juntos em EXPLODE_DURATION,
     * igual a antes da cascata existir.
     */
    setExplosion(target) {
      if (this.explodeTo === target && this.explodeStartedAt === null) return;

      this.groupFrom = this.groupProgress.slice();
      this.liftFrom = this.liftProgress;
      this.explodeTo = target;
      this.explodeStartedAt = performance.now();
      this.activeStagger = this.reduceMotion ? 0 : EXPLODE_STAGGER;
      this.explodeCameraArcFired = false;
      this.viewIsResetting = false;
      this.pendingHomeArc = null;

      const n = this.explodeGroups.length;
      const holdDaSubida = this.activeStagger > 0 ? EXPLODE_DURATION : 0;
      const isExploded = target > 0.5;
      if (isExploded) {
        this.liftOffset = 0;
        this.seqOffset = holdDaSubida;
      } else {
        this.seqOffset = 0;
        this.liftOffset = this.activeStagger > 0
          ? ((n - 1) * this.activeStagger) + EXPLODE_DURATION
          : 0;
      }

      this.clearIdleTimer();
      this.stopIdleRotation();
      if (!isExploded) this.scheduleIdleRotation();
      if (isExploded) this.pullCameraBackForExplosion();
      this.$emit('exploded', isExploded);
    },

    /** Zoom reverso do "Expandir"/reaproximação do "Retrair" (ver
     *  EXPLODE_CAMERA_PULLBACK): mesmo alvo e ângulo atuais, só a distância
     *  muda — funciona igual vindo da vista livre (home) ou de uma peça
     *  focada. */
    nudgeCameraRadius(delta, duration) {
      const target = this.controls.target.clone();
      const spherical = new THREE.Spherical()
        .setFromVector3(this.camera.position.clone().sub(target));
      spherical.radius += delta;
      spherical.makeSafe();
      const toPosition = target.clone().add(new THREE.Vector3().setFromSpherical(spherical));
      this.startCameraArc(target, toPosition, duration);
    },

    pullCameraBackForExplosion() {
      this.nudgeCameraRadius(EXPLODE_CAMERA_PULLBACK, EXPLODE_DURATION);
    },

    /**
     * Deriva piso/logotipo (`liftProgress`) e cada grupo (`groupProgress`)
     * de UM relógio só (`elapsed`), sem timer por grupo (ver §7 da spec) —
     * `setExplosion` já preparou `liftOffset`/`seqOffset`/`activeStagger`
     * para a direção corrente.
     */
    updateExplosion() {
      if (this.explodeStartedAt === null) return;

      const elapsed = performance.now() - this.explodeStartedAt;
      const alvo = this.explodeTo;
      const n = this.explodeGroups.length;

      const tLift = Math.min(1, Math.max(0, (elapsed - this.liftOffset) / EXPLODE_DURATION));
      this.liftProgress = this.liftFrom + (alvo - this.liftFrom) * easeInOut(tLift);

      for (let i = 0; i < n; i += 1) {
        const ordem = alvo > 0.5 ? i : (n - 1 - i);
        const inicio = this.seqOffset + ordem * this.activeStagger;
        const t = Math.min(1, Math.max(0, (elapsed - inicio) / EXPLODE_DURATION));
        this.groupProgress[i] = this.groupFrom[i] + (alvo - this.groupFrom[i]) * easeInOut(t);
      }

      // Câmera só se move na fase de descida (elapsed >= liftOffset), depois
      // que o grupo mais "no topo" (o último a voltar, ver ordem acima) já
      // chegou em casa — nunca no meio da cascata, senão o zoom encolhendo
      // o enquadramento enquanto a peça ainda está em trânsito faz ela
      // parecer mais lenta do que realmente é.
      if (alvo < 0.5 && !this.explodeCameraArcFired && elapsed >= this.liftOffset) {
        this.explodeCameraArcFired = true;
        if (this.pendingHomeArc) {
          this.pendingHomeArc();
          this.pendingHomeArc = null;
        } else if (!this.viewIsResetting) {
          // Retrair pelo botão (não pelo resetView): reaproxima a câmera,
          // o inverso do recuo do "Expandir".
          this.nudgeCameraRadius(-EXPLODE_CAMERA_PULLBACK, EXPLODE_DURATION);
        }
      }

      const total = Math.max(this.liftOffset, this.seqOffset + (n - 1) * this.activeStagger)
        + EXPLODE_DURATION;
      if (elapsed >= total) {
        this.liftProgress = alvo;
        for (let i = 0; i < n; i += 1) this.groupProgress[i] = alvo;
        this.explodeStartedAt = null;
        if (this.pendingModelSwitch) {
          const switchModel = this.pendingModelSwitch;
          this.pendingModelSwitch = null;
          switchModel();
        }
      }

      this.applyExplosion();
    },

    applyExplosion() {
      this.explodeGroups.forEach((grupo, index) => {
        const amount = this.groupProgress[index] * EXPLODE_SCALE;
        grupo.pecas.forEach((peca) => {
          peca.position.copy(peca.userData.home).addScaledVector(peca.userData.explode, amount);
        });
      });

      // Raios de eletricidade do CAD (ver adicionarRaiosEletricidade em
      // transformadorCad.js) são filhos de `group`, não da bucha: sem isto
      // eles ficam para trás na posição de repouso enquanto a bucha se afasta
      // na explosão.
      this.models.forEach((model) => {
        if (model.raios) model.raios.forEach(seguirPecaDoRaio);
      });

      /*
       * O palco é o mesmo nas duas vistas: o disco esmaece até
       * GROUND_OPACITY_EXPLODED, mas nunca desaparece, e o emblema não é
       * tocado. Antes os dois iam a zero — a marca sumia justo na vista em que
       * o usuário passa mais tempo, e o anel verde ficava sozinho no vazio.
       */
      this.groundFloor.material.opacity = GROUND_OPACITY
        - ((GROUND_OPACITY - GROUND_OPACITY_EXPLODED) * this.liftProgress);

      // Desce junto com o palco (ver EXPLODE_LIFT acima): some da vista tão
      // logo o ativo suba o bastante, sem precisar tocar em GROUND_LOGO_Y
      // (a posição de repouso, sem explosão, continua igual).
      const groundDrop = this.liftProgress * GROUND_EXPLODE_DROP;
      this.groundFloor.position.y = GROUND_Y - groundDrop;
      if (this.groundLogo) this.groundLogo.position.y = GROUND_LOGO_Y - groundDrop;
      if (this.groundHalo) this.groundHalo.position.y = GROUND_LOGO_Y - groundDrop;
    },

    startCameraArc(toTarget, toPosition, duration) {
      const fromTarget = this.controls.target.clone();
      const fromSpherical = new THREE.Spherical()
        .setFromVector3(arcOffset.copy(this.camera.position).sub(fromTarget));
      const toSpherical = new THREE.Spherical()
        .setFromVector3(arcOffset.copy(toPosition).sub(toTarget));
      const turn = shortestTurn(fromSpherical.theta, toSpherical.theta);

      this.cameraAnim = {
        fromTarget,
        toTarget: toTarget.clone(),
        fromSpherical,
        toSpherical,
        turn,
        toDistance: toSpherical.radius,
        duration: duration
          || cameraArcDuration(turn, toSpherical.radius - fromSpherical.radius),
        startedAt: performance.now(),
      };
    },

    focusOnPart(id) {
      const anchor = this.findAnchor(id);
      if (!anchor) return;

      // marca que a camera esta enquadrando uma peca, e nao na vista livre —
      // e o que decide se expandir passa por casa (ver toggleExplosion)
      this.focusedPartId = id;
      this.clearIdleTimer();
      this.stopIdleRotation();

      const fixedFraming = (this.explodeTo > 0.5 && FIXED_FRAMINGS_EXPLODED[id])
        || FIXED_FRAMINGS[id];
      if (fixedFraming) {
        this.startCameraArc(fixedFraming.target, fixedFraming.position);
        return;
      }

      const target = this.anchorWorldPosition(anchor);
      const baseOffset = FIXED_ANGLE_PARTS.has(id)
        ? this.initialCameraPosition.clone().sub(this.cameraTarget)
        : this.camera.position.clone().sub(this.controls.target);
      const spherical = new THREE.Spherical().setFromVector3(baseOffset);

      const centered = target.clone();
      centered.x -= ASSET_SHIFT_X;
      if ((centered.x ** 2) + (centered.z ** 2) > 1e-4) {
        const idealTheta = Math.atan2(centered.x, centered.z);
        spherical.theta += shortestTurn(spherical.theta, idealTheta) * FOCUS_ROTATE_RATIO;
      }

      const adjustment = FOCUS_ADJUSTMENTS[id];
      let radiusScale = 1;
      if (adjustment) {
        spherical.theta += adjustment.theta || 0;
        spherical.phi += adjustment.phi || 0;
        radiusScale = adjustment.radiusScale || 1;
      }
      spherical.radius = ORBIT_MIN_DISTANCE * radiusScale;
      spherical.makeSafe();

      const toPosition = target.clone().add(new THREE.Vector3().setFromSpherical(spherical));

      this.startCameraArc(target, toPosition);
    },

    // Desativado por enquanto, junto com o botão (ver CmaDashboard.vue,
    // __save-framing-button / onSaveFramingClick).
    // logCameraFraming() {
    //   const { x, y, z } = this.camera.position;
    //   const { target } = this.controls;
    //   const zoom = this.camera.position.distanceTo(target);
    //   // eslint-disable-next-line no-console
    //   console.log('[CmaAsset3dView] posição da câmera', {
    //     x, y, z, zoom, target: { x: target.x, y: target.y, z: target.z },
    //   });
    // },

    /**
     * Dispara o retrair NA HORA, e não mais depois do arco pra casa: com a
     * cascata, esperar o arco (RESET_VIEW_DURATION) pra só então começar a
     * cascata (até 11+s) somaria as duas durações.
     *
     * O arco em si (câmera pra casa) só começa quando a cascata termina,
     * não junto com ela: se havia uma explosão em andamento (`estavaAtiva`),
     * o arco fica guardado em `pendingHomeArc` e quem dispara é
     * `updateExplosion`, na fase de descida (`elapsed >= liftOffset`),
     * junto do resto do palco. Sem cascata para esperar (já em repouso, ou
     * nada explodido ainda), o arco sai imediatamente, como antes.
     */
    resetView() {
      this.focusedPartId = null;
      const estavaAtiva = this.explodeTo > 0.5 || this.explodeStartedAt !== null;
      this.setExplosion(0);
      this.viewIsResetting = true;

      if (!this.isAtHomePosition()) {
        const arco = () => this.startCameraArc(
          this.cameraTarget, this.initialCameraPosition, RESET_VIEW_DURATION,
        );
        if (estavaAtiva) {
          this.pendingHomeArc = arco;
        } else {
          arco();
        }
      }
      this.scheduleIdleRotation();
    },

    updateCameraFocus() {
      const anim = this.cameraAnim;
      if (!anim) return;

      this.controls.minDistance = 0;
      this.controls.maxDistance = Infinity;

      const t = Math.min(1, (performance.now() - anim.startedAt) / anim.duration);
      const eased = easeInOut(t);

      this.controls.target.lerpVectors(anim.fromTarget, anim.toTarget, eased);

      const { fromSpherical, toSpherical } = anim;
      const bulge = Math.sin(Math.PI * eased) * CAMERA_FOCUS_RADIUS_BULGE;
      arcSpherical.radius = THREE.MathUtils.lerp(fromSpherical.radius, toSpherical.radius, eased)
        + bulge;
      arcSpherical.phi = THREE.MathUtils.lerp(fromSpherical.phi, toSpherical.phi, eased);
      arcSpherical.theta = fromSpherical.theta + anim.turn * eased;
      arcSpherical.makeSafe();
      this.camera.position.setFromSpherical(arcSpherical).add(this.controls.target);

      if (t >= 1) {
        this.cameraAnim = null;
        this.controls.minDistance = Math.min(FREE_ORBIT_MIN_DISTANCE, anim.toDistance);
        this.controls.maxDistance = FREE_ORBIT_MAX_DISTANCE;
      }
    },

    /**
     * Prende o alvo da órbita à caixa de PAN_LIMIT_* em volta do repouso.
     *
     * A câmera anda JUNTO com a correção: deslocar só o alvo mudaria a
     * distância e o ângulo, ou seja, viraria um zoom por fora do limite. Do
     * jeito que está, o enquadramento é idêntico ao de antes do excesso.
     */
    clampPanTarget() {
      const { target } = this.controls;
      panOffset.subVectors(target, this.cameraTarget);
      panClamped.copy(target);

      const horizontal = Math.hypot(panOffset.x, panOffset.z);
      if (horizontal > PAN_LIMIT_RADIUS) {
        const scale = PAN_LIMIT_RADIUS / horizontal;
        panClamped.x = this.cameraTarget.x + panOffset.x * scale;
        panClamped.z = this.cameraTarget.z + panOffset.z * scale;
      }
      panClamped.y = THREE.MathUtils.clamp(target.y, PAN_LIMIT_MIN_Y, PAN_LIMIT_MAX_Y);

      if (panClamped.equals(target)) return;
      this.camera.position.add(panOffset.subVectors(panClamped, target));
      target.copy(panClamped);
    },

    findAnchor(id) {
      const model = this.models[this.activeIndex];
      return model && model.anchors.find(anchor => anchor.id === id);
    },

    /** `anchor.part` normalmente é UM nó (modelo procedural), mas o CAD tem
     *  âncoras de card com vários nós (ex.: as 3 buchas de alta, os 2
     *  candidatos da bolsa — ver construirAncoras em transformadorCad.js).
     *  Uma lista some pra uma lista de 1 sem mudar nada pro caso já existente. */
    anchorParts(anchor) {
      return Array.isArray(anchor.part) ? anchor.part : [anchor.part];
    },

    /** Peças a acender por `highlightPart`/`clearHighlight` — `anchor.destaque`
     *  (calibrado à mão pra âncoras sem nó próprio, ver ANCHOR_HIGHLIGHT_PARTS_CAD
     *  e destacarPecasDasAncoras em transformadorCad.js) tem prioridade sobre
     *  `anchor.part`, que é só a referência de posição do card. */
    anchorHighlightParts(anchor) {
      return anchor.destaque && anchor.destaque.length ? anchor.destaque : this.anchorParts(anchor);
    },

    anchorWorldPosition(anchor, out = new THREE.Vector3()) {
      const model = this.models[this.activeIndex];
      return out.copy(anchor.point)
        .add(this.anchorParts(anchor)[0].position)
        .applyMatrix4(model.group.matrixWorld);
    },

    emitAnchors() {
      if (this.spin) return;

      const model = this.models[this.activeIndex];
      if (!model) return;

      const { width, height } = this.canvasSize;
      const projected = new THREE.Vector3();
      const anchors = model.anchors.map((anchor) => {
        this.anchorWorldPosition(anchor, projected).project(this.camera);
        return {
          id: anchor.id,
          x: Math.round((projected.x * 0.5 + 0.5) * width),
          y: Math.round((-projected.y * 0.5 + 0.5) * height),
          visible: projected.z < 1,
        };
      });

      const signature = JSON.stringify(anchors);
      if (signature === this.lastAnchors) return;
      this.lastAnchors = signature;
      this.$emit('anchors', anchors);
    },

    /** A âncora pode ter mais de uma peça (ver anchorParts) — cada uma tem o
     *  seu próprio `HIGHLIGHT_TARGET`, então a busca é por peça, não uma vez
     *  só pra âncora inteira. É o que deixa 'buchas' pintar as 3 buchas de
     *  alta juntas. */
    highlightPart(id) {
      if (this.highlightedId === id) return;
      this.clearHighlight();

      const anchor = this.findAnchor(id);
      if (!anchor) return;

      this.anchorHighlightParts(anchor).forEach((parte) => {
        const target = parte.getObjectByName(HIGHLIGHT_TARGET) || parte;
        target.traverse((object) => {
          if (!object.isMesh) return;
          object.userData.baseMaterial = object.material;
          object.material = this.getHighlightMaterial(object.material);
        });
      });
      this.highlightedId = id;
    },

    clearHighlight() {
      if (!this.highlightedId) return;
      const anchor = this.findAnchor(this.highlightedId);
      if (anchor) {
        this.anchorHighlightParts(anchor).forEach((parte) => {
          parte.traverse((object) => {
            if (object.userData.baseMaterial) {
              object.material = object.userData.baseMaterial;
              delete object.userData.baseMaterial;
            }
          });
        });
      }
      this.highlightedId = null;
    },

    /** Algumas peças usam um ARRAY de materiais — um por grupo de
     *  geometria/face — em vez de um só. `Material.clone` não existe em
     *  `Array`, então sem este ramo `object.material = getHighlightMaterial(array)`
     *  lançava, parando o `traverse` no meio (as peças seguintes da âncora
     *  ficavam sem highlight, silenciosamente). */
    getHighlightMaterial(baseMaterial) {
      if (Array.isArray(baseMaterial)) {
        return baseMaterial.map(material => this.getHighlightMaterial(material));
      }
      if (!this.highlightMaterials.has(baseMaterial)) {
        const highlighted = baseMaterial.clone();
        highlighted.emissive = srgbColor(UI_AMBER);
        highlighted.emissiveIntensity = 0.38;
        this.highlightMaterials.set(baseMaterial, highlighted);
      }
      return this.highlightMaterials.get(baseMaterial);
    },

    onWindowResize() {
      const { container } = this.$refs;
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!width || !height) return;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
      this.renderer.render(this.scene, this.camera);
      this.canvasSize = { width, height };
      this.lastAnchors = '';
    },

    onControlsInteractionStart() {
      this.clearIdleTimer();
      this.stopIdleRotation();
    },

    onControlsInteractionEnd() {
      this.scheduleIdleRotation();
    },

    isAtHomePosition() {
      return this.camera.position.distanceTo(this.initialCameraPosition) < HOME_POSITION_EPSILON
        && this.controls.target.distanceTo(this.cameraTarget) < HOME_POSITION_EPSILON;
    },

    isAtHomeFraming() {
      return this.controls.target.distanceTo(this.cameraTarget) < HOME_POSITION_EPSILON;
    },

    clearIdleTimer() {
      if (!this.idleTimer) return;
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    },

    scheduleIdleRotation() {
      this.clearIdleTimer();
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        if (this.cameraAnim || this.spin || this.explodeTo > 0.5) return;
        if (!this.isAtHomeFraming()) return;
        this.startIdleRotation();
      }, IDLE_ROTATE_DELAY);
    },

    startIdleRotation() {
      if (this.isAutoRotating) return;
      this.isAutoRotating = true;
      this.controls.autoRotate = true;
      this.controls.autoRotateSpeed = IDLE_ROTATE_SPEED;
      this.$emit('idle-rotate', true);
    },

    stopIdleRotation() {
      if (!this.isAutoRotating) return;
      this.isAutoRotating = false;
      this.controls.autoRotate = false;
      this.$emit('idle-rotate', false);
    },
  },
};
</script>

<style scoped>
.cma-asset-3d-view {
  position: relative;
}

.cma-asset-3d-view ::v-deep canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  outline: none;
}

/* Crossfade curto entre a tela de espera do CAD e o mesh real já
 * texturizado (ver CmaAsset3dLoading.vue e onCadSettled) — o desenho da
 * espera nunca compete visualmente com o modelo final. */
.cma-asset-3d-view__loading-fade-enter-active,
.cma-asset-3d-view__loading-fade-leave-active {
  transition: opacity 0.25s ease;
}

.cma-asset-3d-view__loading-fade-enter,
.cma-asset-3d-view__loading-fade-leave-to {
  opacity: 0;
}
</style>

<!--
  Sem `scoped` de propósito: o painel de debug do SPS (ver buildSpsPanel em
  CmaAsset3dView.vue) é criado com `document.createElement` e anexado direto
  em `document.body`, não no <template> deste componente — então ele NUNCA
  recebe o atributo `data-v-xxxx` que o Vue usa pra casar o CSS `scoped`, e
  essas regras ficariam mortas se estivessem no bloco de cima. `position:
  fixed` (e não `absolute`) pelo mesmo motivo: sendo filho de `<body>`, não
  há mais um container relativo do componente para ancorar o painel.
-->
<style>
.sps-position-panel {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  min-width: 220px;
  background: rgba(20, 22, 24, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #f2f2f2;
  font-size: 12px;
  line-height: 1.2;
}

.sps-position-panel__title {
  margin-bottom: 2px;
}

.sps-position-panel__row {
  display: grid;
  grid-template-columns: 14px 1fr 52px;
  align-items: center;
  gap: 8px;
}

.sps-position-panel__row input[type='range'] {
  width: 100%;
}

.sps-position-panel__value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.sps-position-panel__save {
  margin-top: 4px;
  padding: 4px 8px;
  background: #f9a825;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.sps-position-panel__save:hover {
  filter: brightness(1.05);
}

/* Painel de desempenho (ver models/painelDesempenho.js) — mesmo motivo de
 * `position: fixed` e ausência de `scoped` do painel do SPS acima: anexado
 * direto em `document.body`, nunca recebe o `data-v-xxxx` do CSS scoped. */
.cma-perf-hud {
  position: fixed;
  bottom: 12px;
  left: 12px;
  z-index: 9999;
  min-width: 230px;
  padding: 8px 10px;
  background: rgba(20, 22, 24, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #f2f2f2;
  font-size: 11px;
  line-height: 1.35;
}

.cma-perf-hud__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.cma-perf-hud__toggle {
  width: 18px;
  height: 18px;
  line-height: 16px;
  padding: 0;
  background: rgba(255, 255, 255, 0.12);
  color: inherit;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
}

.cma-perf-hud__toggle:hover {
  background: rgba(255, 255, 255, 0.22);
}

.cma-perf-hud__body {
  margin: 0;
  /* Bootstrap (reboot.css) define `pre { color: ... }` própria — uma regra
   * DIRETA no elemento, que vence a cor herdada de `.cma-perf-hud`
   * (color: #f2f2f2) não importa a especificidade, porque herança nunca
   * compete no cascade contra uma declaração explícita no próprio elemento.
   * Sem isto o texto saía escuro sobre o fundo escuro do painel. */
  color: #f2f2f2;
  font-family: Consolas, Menlo, monospace;
  white-space: pre;
}

.cma-perf-hud--collapsed .cma-perf-hud__body {
  display: none;
}
</style>
